import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  FileText, Send, CheckCircle, Clock, DollarSign, 
  Eye, Calendar, CalendarClock, ChevronDown,
  ArrowRight, RefreshCw, Mail, MessageSquare, AlertCircle,
  Plus, X, User, Trash2, Edit2, Percent, XCircle, TrendingUp, Link2,
  Play, Pause, Square, Search, MapPin, Flame, Phone, PhoneOff, Settings2, ShieldCheck, Filter, Copy, MoreHorizontal
} from "lucide-react";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import FlexibleScheduleBadge from "@/components/FlexibleScheduleBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { getSessionToken } from "@/utils/auth";
import { formatDate, formatDateTime } from "@/utils/date";
import { ChannelBadge } from "@/components/ChannelTable";
import { LeadSourcePicker } from "@/components/LeadSourcePicker";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Compact button used inside the estimate-card "More" popover. Same look
// as a shadcn dropdown item — keeps the card small when collapsed and the
// menu tidy when open.
function MoreMenuItem({ icon: Icon, label, onClick, testId, tone }) {
  const toneClass =
    tone === "danger"
      ? "text-red-600 hover:bg-red-50"
      : "text-zinc-700 hover:bg-zinc-100";
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md text-left ${toneClass}`}
    >
      {Icon && <Icon className="w-4 h-4 shrink-0" />}
      <span className="truncate">{label}</span>
    </button>
  );
}

const STATUS_COLORS = {
  new: "bg-amber-100 text-amber-700",
  nurturing: "bg-purple-100 text-purple-700",
  scheduled: "bg-cyan-100 text-cyan-700",
  reschedule_needed: "bg-orange-100 text-orange-800",
  converted: "bg-green-100 text-green-700",
  expired: "bg-zinc-100 text-zinc-500",
  lost: "bg-red-100 text-red-700",
  non_responsive: "bg-orange-100 text-orange-700",
  hot: "bg-rose-100 text-rose-600",
  phone_follow_up: "bg-blue-100 text-blue-700",
  drip_finished: "bg-yellow-100 text-yellow-700",
  // Legacy status mappings
  sent: "bg-amber-100 text-amber-700",
  followed_up: "bg-purple-100 text-purple-700",
};

// Helper to convert UTC date to PST Date object
const toPSTDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  // Convert to PST string then back to Date for correct day comparison
  const pstString = date.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
  const pstDate = new Date(pstString);
  return new Date(pstDate.getFullYear(), pstDate.getMonth(), pstDate.getDate());
};

const STATUS_ICONS = {
  new: Clock,
  nurturing: RefreshCw,
  scheduled: Calendar,
  reschedule_needed: CalendarClock,
  converted: CheckCircle,
  expired: AlertCircle,
  lost: XCircle,
  non_responsive: AlertCircle,
  hot: Flame,
  phone_follow_up: Phone,
  drip_finished: PhoneOff,
  // Legacy status mappings
  sent: Clock,
  followed_up: RefreshCw,
};

const STATUS_LABELS = {
  new: "New",
  nurturing: "Nurturing",
  scheduled: "Scheduled",
  reschedule_needed: "Reschedule Needed",
  converted: "Converted",
  expired: "Expired",
  lost: "Lost",
  non_responsive: "Non-Responsive",
  hot: "Hot",
  phone_follow_up: "Phone Follow-Up",
  drip_finished: "Drip Finished",
  // Legacy
  sent: "New",
  followed_up: "Nurturing",
};

// Preset line items (same as invoices)
const DEFAULT_PRESET_LINE_ITEMS = [
  { name: "Window Rescreen", unit_price: 50, taxable: false },
  { name: "New Window Screen", unit_price: 70, taxable: true },
  { name: "Sliding Door Rescreen", unit_price: 110, taxable: false },
  { name: "New Sliding Door Screen", unit_price: 365, taxable: true },
  { name: "PetScreen Upgrade", unit_price: 25, taxable: true },
];

const DATE_PRESETS = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 Days" },
  { value: "thisMonth", label: "This Month" },
  { value: "lastMonth", label: "Last Month" },
  { value: "custom", label: "Custom Range" },
];

const getDateRange = (preset) => {
  // Get current time in PST
  const now = new Date();
  const pstString = now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
  const pstNow = new Date(pstString);
  const today = new Date(pstNow.getFullYear(), pstNow.getMonth(), pstNow.getDate());
  // End-of-day helper — creates a new Date object at 23:59:59.999 so
  // start !== end and inclusive ranges span the full day. Critical: the
  // backend `/estimates/stats` endpoint receives these as ISO strings and
  // does a `$gte / $lte` range match on `created_at`. If start === end
  // (both midnight), the range is zero-width and returns 0 rows — that
  // was the "Today/Yesterday shows 0 in KPIs but list has rows" bug.
  const endOfDay = (d) => {
    const eod = new Date(d);
    eod.setHours(23, 59, 59, 999);
    return eod;
  };

  switch (preset) {
    case "today":
      return { start: today, end: endOfDay(today) };
    case "yesterday": {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { start: yesterday, end: endOfDay(yesterday) };
    }
    case "last7": {
      const last7Start = new Date(today);
      last7Start.setDate(last7Start.getDate() - 6);
      return { start: last7Start, end: endOfDay(today) };
    }
    case "thisMonth": {
      const thisMonthStart = new Date(pstNow.getFullYear(), pstNow.getMonth(), 1);
      return { start: thisMonthStart, end: endOfDay(today) };
    }
    case "lastMonth": {
      const lastMonthStart = new Date(pstNow.getFullYear(), pstNow.getMonth() - 1, 1);
      const lastMonthEnd = new Date(pstNow.getFullYear(), pstNow.getMonth(), 0);
      return { start: lastMonthStart, end: endOfDay(lastMonthEnd) };
    }
    default:
      return null;
  }
};

export default function Estimates() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estimates, setEstimates] = useState([]);
  const [filteredEstimates, setFilteredEstimates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active"); // Default to show non-converted/non-closed estimates
  const [pricingItems, setPricingItems] = useState([]); // Quick add items from Settings

  // High-value threshold: estimates with total >= this get a badge and
  // can be filtered via the "High Value" chip. Configurable in Settings;
  // default $1,000. `thresholdDraft` holds unsaved edits inside the
  // popover so the input stays snappy.
  const [highValueThreshold, setHighValueThreshold] = useState(1000);
  const [thresholdDraft, setThresholdDraft] = useState("1000");
  const [savingThreshold, setSavingThreshold] = useState(false);

  // Fake-phone scan state — admin cleanup tool. Kept opt-in (never runs
  // on load) so it doesn't burn Twilio Lookup credits automatically.
  const [fakeScanning, setFakeScanning] = useState(false);
  const [fakeScanResult, setFakeScanResult] = useState(null);
  const [fakeScanOpen, setFakeScanOpen] = useState(false);
  const [deletingLeadIds, setDeletingLeadIds] = useState(new Set());

  // Advanced filters — dollar range, service inclusion, zip code. All
  // optional; blank means "no constraint". Applied inside
  // searchFilteredEstimates so counts, filter chips, and the list all
  // reflect them consistently.
  const [minDollar, setMinDollar] = useState("");
  const [maxDollar, setMaxDollar] = useState("");
  const [includesService, setIncludesService] = useState("");
  const [zipFilter, setZipFilter] = useState("");
  const activeFilterCount = (
    (minDollar ? 1 : 0) + (maxDollar ? 1 : 0) +
    (includesService.trim() ? 1 : 0) + (zipFilter.trim() ? 1 : 0)
  );
  const clearAdvancedFilters = () => {
    setMinDollar(""); setMaxDollar(""); setIncludesService(""); setZipFilter("");
  };
  
  // Date filtering
  const [datePreset, setDatePreset] = useState("all");
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Search
  const [searchQuery, setSearchQuery] = useState("");
  
  // Landline scan
  const [scanningLandlines, setScanningLandlines] = useState(false);
  // Dupe sweep
  const [dupeOpen, setDupeOpen] = useState(false);
  const [dupeLoading, setDupeLoading] = useState(false);
  const [dupeGroups, setDupeGroups] = useState([]);
  const [dupeHours, setDupeHours] = useState(24);
  const [mergingKey, setMergingKey] = useState(null);
  const [scanningDrip, setScanningDrip] = useState(false);
  
  // Dialog states
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  // Call-log composer state — session only. `callLogOpen` opens the small
  // note field inside the details modal; `callNote` is the free-text note
  // (e.g. "left VM"); `callLogSaving` blocks a double-tap.
  const [callLogOpen, setCallLogOpen] = useState(false);
  const [callNote, setCallNote] = useState("");
  const [callLogSaving, setCallLogSaving] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  // Send options
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSMS, setSendSMS] = useState(true);
  // Every quote now goes out with an estimate link — customers view the
  // quote on the funnel page, not inline in the SMS/email. The only
  // remaining choice is whether that link lets them book their own
  // appointment or is view-only (admin books manually).
  const [allowBooking, setAllowBooking] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Follow-up state
  const [followUpLoading, setFollowUpLoading] = useState(null);
  
  // Sequence control state
  const [sequenceLoading, setSequenceLoading] = useState(null);
  
  // Create/Edit estimate form
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [newEstimate, setNewEstimate] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    notes: "",
    line_items: [{ name: "", quantity: 1, unit_price: 0, taxable: false }],
    discount_type: "",
    discount_value: 0,
    discount_description: ""
  });

  // Check for customer data passed from Customers page
  useEffect(() => {
    if (location.state?.createEstimate && location.state?.customer) {
      const customer = location.state.customer;
      setSelectedCustomerId(customer.customer_id || "");
      setNewEstimate(prev => ({
        ...prev,
        customer_name: customer.fullName || "",
        customer_phone: customer.phoneNumber || "",
        customer_address: customer.address || ""
      }));
      setCreateDialogOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Handle viewing a specific estimate passed from Customers page
  useEffect(() => {
    if (location.state?.viewEstimateId && estimates.length > 0) {
      const estimateToView = estimates.find(e => e.lead_id === location.state.viewEstimateId);
      if (estimateToView) {
        setSelectedEstimate(estimateToView);
        setViewDialogOpen(true);
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state?.viewEstimateId, estimates]);

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (user) {
      fetchEstimates();
      fetchStats();
      fetchCustomers();
      fetchPricingItems();
      fetchHighValueThreshold();
    }
  }, [user]);

  // Filter estimates by date
  useEffect(() => {
    if (datePreset === "all") {
      setFilteredEstimates(estimates);
    } else if (datePreset === "custom") {
      if (customDateRange.from && customDateRange.to) {
        const filtered = estimates.filter(est => {
          const estDate = toPSTDate(est.created_at);
          if (!estDate) return false;
          const from = new Date(customDateRange.from);
          from.setHours(0, 0, 0, 0);
          const to = new Date(customDateRange.to);
          to.setHours(23, 59, 59, 999);
          return estDate >= from && estDate <= to;
        });
        setFilteredEstimates(filtered);
      } else {
        setFilteredEstimates(estimates);
      }
    } else {
      const range = getDateRange(datePreset);
      if (range) {
        const filtered = estimates.filter(est => {
          const estDate = toPSTDate(est.created_at);
          if (!estDate) return false;
          return estDate >= range.start && estDate <= range.end;
        });
        setFilteredEstimates(filtered);
      } else {
        setFilteredEstimates(estimates);
      }
    }
  }, [datePreset, customDateRange, estimates]);

  // Re-fetch server-side KPI stats whenever the date filter changes so
  // the headline numbers stay locked to the visible filter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user) fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datePreset, customDateRange, user]);

  const fetchUser = async () => {
    try {
      const token = getSessionToken();
      if (!token) {
        navigate("/login");
        return;
      }
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.role !== "Admin") {
        toast.error("Access denied - Admin only");
        navigate("/jobs");
        return;
      }
      setUser(response.data);
    } catch (error) {
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const fetchEstimates = async () => {
    try {
      const token = getSessionToken();
      // Always fetch ALL estimates - filtering is done client-side
      const response = await axios.get(`${API}/estimates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEstimates(response.data);
    } catch (error) {
      console.error("Failed to fetch estimates:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = getSessionToken();
      // Resolve current date filter to ISO range so the server-side
      // aggregation matches the client-side filtered list exactly.
      const params = {};
      if (datePreset === "custom") {
        if (customDateRange.from && customDateRange.to) {
          const from = new Date(customDateRange.from); from.setHours(0, 0, 0, 0);
          const to = new Date(customDateRange.to);   to.setHours(23, 59, 59, 999);
          params.date_from = from.toISOString();
          params.date_to = to.toISOString();
        }
      } else if (datePreset !== "all") {
        const range = getDateRange(datePreset);
        if (range) {
          params.date_from = range.start.toISOString();
          params.date_to = range.end.toISOString();
        }
      }
      const response = await axios.get(`${API}/estimates/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const fetchPricingItems = async () => {
    try {
      const token = getSessionToken();
      // Fetch ALL items (not just quick-add) so the "Select service" dropdown
      // still shows everything even after admin unchecks Quick Add on a row.
      // Previously used /pricing-items/quick-add which filtered BOTH the chips
      // AND the dropdown — that was the "unchecking removes from dropdown" bug.
      const response = await axios.get(`${API}/pricing-items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Transform API items to match preset format. Keep the raw
      // show_on_quick_add flag so the chip row can filter locally.
      const items = response.data.filter(item => !item.archived && item.item_type !== "discount").map(item => ({
        name: item.name,
        unit_price: item.price,
        taxable: item.is_taxed,
        show_on_quick_add: item.show_on_quick_add !== false,
        order: item.order,
      }));
      setPricingItems(items.length > 0 ? items : DEFAULT_PRESET_LINE_ITEMS);
    } catch (error) {
      // Fall back to default presets if API fails
      setPricingItems(DEFAULT_PRESET_LINE_ITEMS);
    }
  };

  const fetchHighValueThreshold = async () => {
    try {
      const token = getSessionToken();
      const { data } = await axios.get(`${API}/settings/high-value-threshold`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const val = Number(data?.threshold) || 1000;
      setHighValueThreshold(val);
      setThresholdDraft(String(val));
    } catch (_e) {
      // Non-admins get 403 — fine, they don't need to change it. Leave
      // default $1,000 in local state.
    }
  };

  const saveHighValueThreshold = async () => {
    const parsed = Number(thresholdDraft);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.error("Enter a valid dollar amount (0 or higher).");
      return;
    }
    setSavingThreshold(true);
    try {
      const token = getSessionToken();
      const { data } = await axios.put(
        `${API}/settings/high-value-threshold`,
        { threshold: parsed },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setHighValueThreshold(Number(data.threshold));
      toast.success(`High-value threshold set to $${Number(data.threshold).toLocaleString()}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update threshold.");
    } finally {
      setSavingThreshold(false);
    }
  };

  const scanForLandlines = async () => {
    setScanningLandlines(true);
    try {
      const token = getSessionToken();
      const response = await axios.post(`${API}/estimates/scan-landlines`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      const landlineCount = data.landlines?.length || 0;
      if (landlineCount > 0) {
        const names = data.landlines.map(l => l.name).join(", ");
        toast.success(`Found ${landlineCount} landline(s): ${names}. Moved to Phone Follow-Up.`);
        fetchEstimates();
      } else {
        toast.success(`Scanned ${data.total_checked} leads — no landlines found. ${data.already_checked} previously checked.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to scan for landlines");
    } finally {
      setScanningLandlines(false);
    }
  };

  const scanForDripFinished = async () => {
    setScanningDrip(true);
    try {
      const token = getSessionToken();
      const response = await axios.post(`${API}/estimates/scan-drip-finished`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      const finishedCount = data.drip_finished?.length || 0;
      if (finishedCount > 0) {
        const names = data.drip_finished.map(l => l.name).join(", ");
        toast.success(`Found ${finishedCount} drip-finished lead(s): ${names}. Ready for final call.`);
        fetchEstimates();
      } else {
        toast.success(`Checked ${data.total_checked} leads — ${data.still_in_drip} still in drip, ${data.no_drip} never dripped.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to scan drip status");
    } finally {
      setScanningDrip(false);
    }
  };

  // ---------- Dupe Sweep ----------
  const openDupeSweep = async (hoursOverride) => {
    const hrs = hoursOverride ?? dupeHours;
    setDupeOpen(true);
    setDupeLoading(true);
    try {
      const token = getSessionToken();
      const { data } = await axios.get(`${API}/estimates/duplicate-groups`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { hours: hrs },
      });
      setDupeGroups(data.groups || []);
      if ((data.groups || []).length === 0) {
        toast.success(`No duplicate estimates found in the last ${hrs}h — you're clean.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load duplicates");
    } finally {
      setDupeLoading(false);
    }
  };

  const mergeGroup = async (group) => {
    if (!window.confirm(
      `Keep the newest estimate for ${group.keeper.full_name || group.normalized_phone} and delete ${group.duplicates.length} older duplicate(s)?`
    )) return;
    setMergingKey(group.normalized_phone);
    try {
      const token = getSessionToken();
      const { data } = await axios.post(`${API}/estimates/merge-duplicates`, {
        keeper_lead_id: group.keeper.lead_id,
        duplicate_lead_ids: group.duplicates.map(d => d.lead_id),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Merged — deleted ${data.deleted_count} duplicate(s). Kept ${group.keeper.full_name}.`);
      // Optimistic remove of the group locally
      setDupeGroups(prev => prev.filter(g => g.normalized_phone !== group.normalized_phone));
      fetchEstimates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to merge duplicates");
    } finally {
      setMergingKey(null);
    }
  };


  // Handle customer selection
  const handleCustomerSelect = (customerId) => {
    setSelectedCustomerId(customerId);
    if (customerId === "") {
      setNewEstimate(prev => ({
        ...prev,
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        customer_address: ""
      }));
    } else {
      const customer = customers.find(c => c.customer_id === customerId);
      if (customer) {
        setNewEstimate(prev => ({
          ...prev,
          customer_name: customer.fullName || "",
          customer_phone: customer.phoneNumber || "",
          customer_address: customer.address || ""
        }));
      }
    }
  };

  // Create estimate
  const handleCreateEstimate = async () => {
    if (!newEstimate.customer_name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    
    const validLineItems = newEstimate.line_items.filter(item => 
      item.name.trim() && item.quantity > 0 && item.unit_price > 0
    );
    
    if (validLineItems.length === 0) {
      toast.error("At least one valid line item is required");
      return;
    }

    try {
      const token = getSessionToken();
      await axios.post(`${API}/estimates`, {
        customer_name: newEstimate.customer_name.trim(),
        customer_email: newEstimate.customer_email.trim() || null,
        customer_phone: newEstimate.customer_phone.trim() || null,
        customer_address: newEstimate.customer_address.trim() || null,
        customer_id: selectedCustomerId || null,
        notes: newEstimate.notes.trim() || null,
        line_items: validLineItems,
        tax_rate: 0.0875,
        discount_type: newEstimate.discount_type || null,
        discount_value: newEstimate.discount_value || null,
        discount_description: newEstimate.discount_description || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Estimate created");
      setCreateDialogOpen(false);
      setSelectedCustomerId("");
      setNewEstimate({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        customer_address: "",
        notes: "",
        line_items: [{ name: "", quantity: 1, unit_price: 0, taxable: false }],
        discount_type: "",
        discount_value: 0,
        discount_description: ""
      });
      fetchEstimates();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create estimate");
    }
  };

  // Line item helpers
  const addLineItem = () => {
    setNewEstimate(prev => ({
      ...prev,
      line_items: [...prev.line_items, { name: "", quantity: 1, unit_price: 0, taxable: false }]
    }));
  };

  const addPresetLineItem = (preset) => {
    setNewEstimate(prev => ({
      ...prev,
      line_items: [...prev.line_items, { 
        name: preset.name, 
        quantity: 1, 
        unit_price: preset.unit_price, 
        taxable: preset.taxable 
      }]
    }));
  };

  const removeLineItem = (index) => {
    if (newEstimate.line_items.length <= 1) return;
    setNewEstimate(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const updateLineItem = (index, field, value) => {
    setNewEstimate(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handlePresetSelect = (index, presetName) => {
    const preset = pricingItems.find(p => p.name === presetName);
    if (preset) {
      setNewEstimate(prev => ({
        ...prev,
        line_items: prev.line_items.map((item, i) => 
          i === index ? { 
            name: preset.name, 
            quantity: item.quantity || 1, 
            unit_price: preset.unit_price, 
            taxable: preset.taxable 
          } : item
        )
      }));
    }
  };

  const calculateNewEstimateTotals = () => {
    const validItems = newEstimate.line_items.filter(item => 
      item.name.trim() && item.quantity > 0 && item.unit_price > 0
    );
    const subtotal = validItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const taxableSubtotal = validItems.filter(item => item.taxable).reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    
    // Calculate discount
    let discountAmount = 0;
    if (newEstimate.discount_type === "dollar" && newEstimate.discount_value > 0) {
      discountAmount = newEstimate.discount_value;
    } else if (newEstimate.discount_type === "percent" && newEstimate.discount_value > 0) {
      discountAmount = subtotal * (newEstimate.discount_value / 100);
    }
    
    const subtotalAfterDiscount = subtotal - discountAmount;
    const taxableAfterDiscount = Math.max(0, taxableSubtotal - discountAmount);
    const tax = taxableAfterDiscount * 0.0875;
    const total = subtotalAfterDiscount + tax;
    
    return { subtotal, taxableSubtotal, discountAmount, subtotalAfterDiscount, tax, total };
  };

  // Edit existing estimate
  const openEditDialog = (estimate) => {
    const estimateData = estimate.estimate_data || {};
    setSelectedEstimate(estimate);
    setNewEstimate({
      customer_name: estimate.full_name || "",
      customer_email: estimate.email || "",
      customer_phone: estimate.phone || "",
      customer_address: estimate.address || "",
      notes: estimate.notes || "",
      line_items: estimateData.line_items?.length > 0 
        ? estimateData.line_items.map(item => ({
            name: item.name || "",
            quantity: item.quantity || 1,
            unit_price: item.unit_price || 0,
            taxable: item.taxable || false
          }))
        : [{ name: "", quantity: 1, unit_price: 0, taxable: false }],
      discount_type: estimateData.discount_type || "",
      discount_value: estimateData.discount_value || 0,
      discount_description: estimateData.discount_description || ""
    });
    setEditDialogOpen(true);
  };

  const handleUpdateEstimate = async () => {
    if (!selectedEstimate) return;
    
    const validLineItems = newEstimate.line_items.filter(item => 
      item.name.trim() && item.quantity > 0 && item.unit_price > 0
    );
    
    if (validLineItems.length === 0) {
      toast.error("At least one valid line item is required");
      return;
    }

    try {
      const token = getSessionToken();
      await axios.put(`${API}/estimates/${selectedEstimate.lead_id}`, {
        line_items: validLineItems,
        discount_type: newEstimate.discount_type || null,
        discount_value: newEstimate.discount_value || null,
        discount_description: newEstimate.discount_description || null,
        notes: newEstimate.notes.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Estimate updated");
      setEditDialogOpen(false);
      setSelectedEstimate(null);
      setNewEstimate({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        customer_address: "",
        notes: "",
        line_items: [{ name: "", quantity: 1, unit_price: 0, taxable: false }],
        discount_type: "",
        discount_value: 0,
        discount_description: ""
      });
      fetchEstimates();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update estimate");
    }
  };

  const handleSendEstimate = async () => {
    if (!sendEmail && !sendSMS) {
      toast.error("Please select at least one delivery method");
      return;
    }

    setSending(true);
    try {
      const token = getSessionToken();
      await axios.post(
        `${API}/estimates/${selectedEstimate.lead_id}/send`,
        { send_email: sendEmail, send_sms: sendSMS, include_booking_link: true, allow_booking: allowBooking },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Estimate sent successfully!");
      setSendDialogOpen(false);
      fetchEstimates();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send estimate");
    } finally {
      setSending(false);
    }
  };

  const handleConvertToInvoice = async (estimate) => {
    try {
      const token = getSessionToken();
      const response = await axios.post(
        `${API}/estimates/${estimate.lead_id}/convert`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Created ${response.data.invoice_number}`);
      navigate('/invoices');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert estimate");
    }
  };

  const handleMarkScheduled = async (estimate) => {
    try {
      const token = getSessionToken();
      await axios.put(
        `${API}/estimates/${estimate.lead_id}/status`,
        { status: "scheduled" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Estimate marked as scheduled");
      fetchEstimates();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleUpdateStatus = async (leadId, newStatus) => {
    try {
      const token = getSessionToken();
      await axios.put(
        `${API}/estimates/${leadId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Status updated to ${STATUS_LABELS[newStatus] || newStatus}`);
      fetchEstimates();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleMarkLost = async (estimate) => {
    try {
      const token = getSessionToken();
      await axios.put(
        `${API}/estimates/${estimate.lead_id}/status`,
        { status: "lost" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Estimate marked as lost");
      fetchEstimates();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleMarkNonResponsive = async (estimate) => {
    try {
      const token = getSessionToken();
      await axios.put(
        `${API}/estimates/${estimate.lead_id}/status`,
        { status: "non_responsive" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Estimate marked as non-responsive");
      fetchEstimates();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const handleDeleteEstimate = async (force = false) => {    if (!selectedEstimate) return;
    
    try {
      const token = getSessionToken();
      const url = force 
        ? `${API}/estimates/${selectedEstimate.lead_id}?force=true`
        : `${API}/estimates/${selectedEstimate.lead_id}`;
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(force ? "Estimate force deleted" : "Estimate deleted");
      setDeleteDialogOpen(false);
      setSelectedEstimate(null);
      fetchEstimates();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete estimate");
    }
  };

  // "Xh ago" / "Yd ago" — short relative time for the call-log button
  // pill. Falls back to a plain date for anything older than a week.
  const formatRelative = (iso) => {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const diffMin = Math.floor((Date.now() - then) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  // Log a call attempt on the currently-selected estimate. Passing an
  // empty note is fine — the backend stores just the timestamp so admins
  // can rapid-log with one tap ("Save" → done) when they don't need copy.
  const handleLogCall = async () => {
    if (!selectedEstimate || callLogSaving) return;
    setCallLogSaving(true);
    try {
      const token = getSessionToken();
      const { data } = await axios.post(
        `${API}/estimates/${selectedEstimate.lead_id}/call-log`,
        { note: callNote.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Merge the fresh call log fields into the currently open modal so
      // the header pill updates without a full refetch.
      setSelectedEstimate((prev) => prev ? {
        ...prev,
        call_logs: data.call_logs || [],
        last_called_at: data.last_called_at,
        last_call_note: data.last_call_note,
      } : prev);
      setCallNote("");
      setCallLogOpen(false);
      toast.success("Call logged");
      fetchEstimates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to log call");
    } finally {
      setCallLogSaving(false);
    }
  };

  const handleDeleteCallLog = async (loggedAt) => {
    if (!selectedEstimate || !loggedAt) return;
    if (!window.confirm("Remove this call entry?")) return;
    try {
      const token = getSessionToken();
      await axios.delete(
        `${API}/estimates/${selectedEstimate.lead_id}/call-log/${encodeURIComponent(loggedAt)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refetch this estimate's fresh log entries.
      const { data: fresh } = await axios.get(
        `${API}/estimates/${selectedEstimate.lead_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedEstimate(fresh);
      fetchEstimates();
    } catch (err) {
      toast.error("Failed to remove call entry");
    }
  };

  /**
   * Admin cleanup helper. Fires the backend scan (heuristics + Twilio
   * Lookup) and opens a modal listing flagged leads with delete buttons.
   * The scan itself never deletes anything — the admin approves each
   * removal individually.
   */
  const runFakePhoneScan = async () => {
    setFakeScanning(true);
    try {
      const token = getSessionToken();
      const { data } = await axios.post(
        `${API}/leads/scan-fake-phones`,
        { include_lookup: true, limit: 500 },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setFakeScanResult(data);
      setFakeScanOpen(true);
      if (data.flagged_count === 0) {
        toast.success(`Scanned ${data.scanned} — no fake numbers found!`);
      } else {
        toast.info(`Scanned ${data.scanned}, flagged ${data.flagged_count}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally {
      setFakeScanning(false);
    }
  };

  const deleteFlaggedLead = async (leadId) => {
    setDeletingLeadIds(prev => new Set(prev).add(leadId));
    try {
      const token = getSessionToken();
      // force=true so converted-status guard doesn't stop cleanup
      await axios.delete(`${API}/estimates/${leadId}?force=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Remove locally so the row disappears without a re-scan
      setFakeScanResult(prev => prev ? {
        ...prev,
        flagged: prev.flagged.filter(r => r.lead_id !== leadId),
        flagged_count: Math.max(0, (prev.flagged_count || 1) - 1),
      } : prev);
      toast.success("Deleted");
      fetchEstimates();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingLeadIds(prev => {
        const next = new Set(prev);
        next.delete(leadId);
        return next;
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Calculate stats from date-filtered estimates (ALL statuses - not affected by status filter)
  const getFilteredStats = () => {
    // Combine "new" and "sent" (legacy) into one count
    const newEst = filteredEstimates.filter(e => ["new", "sent", null, undefined].includes(e.estimate_status) || !e.estimate_status);
    const nurturing = filteredEstimates.filter(e => ["nurturing", "followed_up"].includes(e.estimate_status));
    const scheduled = filteredEstimates.filter(e => e.estimate_status === "scheduled");
    const rescheduleNeeded = filteredEstimates.filter(e => e.estimate_status === "reschedule_needed");
    const converted = filteredEstimates.filter(e => e.estimate_status === "converted");
    const expired = filteredEstimates.filter(e => e.estimate_status === "expired");
    const lost = filteredEstimates.filter(e => e.estimate_status === "lost");
    const hot = filteredEstimates.filter(e => e.estimate_status === "hot");
    const phoneFollowUp = filteredEstimates.filter(e => e.estimate_status === "phone_follow_up");
    const dripFinished = filteredEstimates.filter(e => e.estimate_status === "drip_finished");
    
    const total = newEst.length + nurturing.length + scheduled.length + rescheduleNeeded.length + converted.length + expired.length + lost.length + hot.length + phoneFollowUp.length + dripFinished.length;
    const conversionPercent = total > 0 ? Math.round((converted.length / total) * 100) : 0;
    
    // Total $ value across ALL estimates in the filtered set (every status,
    // including lost) — answers the "what's the lifetime value pitched?"
    // question alongside the 391-count KPI.
    const allTimeValue = filteredEstimates.reduce(
      (sum, e) => sum + (e.estimate_data?.total || 0),
      0
    );
    
    const convertedTotal = converted.reduce((sum, e) => sum + (e.estimate_data?.total || 0), 0);
    
    // NEW METRICS
    // Pending Estimates: NEW + NURTURING + HOT
    const pendingEstimates = [...newEst, ...nurturing, ...hot];
    const pendingCount = pendingEstimates.length;
    
    // Pending Value: Sum of estimate totals for NEW + NURTURING + HOT
    const pendingValue = pendingEstimates.reduce((sum, e) => sum + (e.estimate_data?.total || 0), 0);
    
    // Scheduled Value: Sum of estimate totals for SCHEDULED estimates
    const scheduledValue = scheduled.reduce((sum, e) => sum + (e.estimate_data?.total || 0), 0);
    
    // Won Revenue: Sum of converted_invoice_total for CONVERTED estimates (from invoice)
    // Note: This uses the invoice total stored on the estimate, or falls back to estimate total
    const wonRevenue = converted.reduce((sum, e) => {
      // Use converted_invoice_total if available, otherwise estimate total
      return sum + (e.converted_invoice_total || e.estimate_data?.total || 0);
    }, 0);
    
    // Needs Follow-Up: NEW or NURTURING or HOT with last_followed_up_at > 3 days ago or null
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
    
    const needsFollowUp = pendingEstimates.filter(e => {
      const lastFollowUp = e.last_followed_up_at;
      if (!lastFollowUp) return true; // Never followed up
      const lastDate = new Date(lastFollowUp);
      return lastDate < threeDaysAgo;
    }).length;
    
    return {
      counts: {
        new: newEst.length,
        nurturing: nurturing.length,
        scheduled: scheduled.length,
        reschedule_needed: rescheduleNeeded.length,
        converted: converted.length,
        expired: expired.length,
        lost: lost.length,
        hot: hot.length,
        phone_follow_up: phoneFollowUp.length,
        drip_finished: dripFinished.length,
        total
      },
      conversionPercent,
      convertedTotal,
      allTimeValue,
      // New metrics
      pendingCount,
      pendingValue,
      scheduledValue,
      wonRevenue,
      needsFollowUp
    };
  };

  const clientStats = getFilteredStats();

  // Prefer server-side aggregated stats when present — they're computed
  // over the FULL estimates collection (no row-cap) so the headline KPI
  // card and per-status tab counts stay accurate even after the row list
  // outgrows whatever cap the list endpoint uses. Falls back to the
  // client-side reduce while `stats` is still loading or if the call
  // failed (e.g. offline).
  const filteredStats = stats?.counts ? {
    counts: stats.counts,
    conversionPercent: stats.conversionPercent ?? clientStats.conversionPercent,
    convertedTotal: stats.convertedTotal ?? clientStats.convertedTotal,
    allTimeValue: stats.allTimeValue ?? clientStats.allTimeValue,
    pendingCount: stats.pendingCount ?? clientStats.pendingCount,
    pendingValue: stats.pendingValue ?? clientStats.pendingValue,
    scheduledValue: stats.scheduledValue ?? clientStats.scheduledValue,
    wonRevenue: stats.wonRevenue ?? clientStats.wonRevenue,
    // needsFollowUp requires per-record inspection (last_followed_up_at),
    // so it's still derived client-side from the rendered list — the
    // pending bucket is small enough that the row-cap won't affect it.
    needsFollowUp: clientStats.needsFollowUp,
  } : clientStats;

  // Apply search filter
  const searchFilteredEstimates = (() => {
    // Start from full set; text-search narrows first, then advanced
    // filters (dollar range, service, zip) narrow further. All are
    // applied together so counts/chips stay in sync with the list.
    let list = filteredEstimates;

    // Text search (name / phone / address / email)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const queryDigits = query.replace(/\D/g, "");
      list = list.filter(est => {
        const name = (est.full_name || "").toLowerCase();
        const phone = (est.phone || "").replace(/\D/g, "");
        const address = (est.address || "").toLowerCase();
        const email = (est.email || "").toLowerCase();
        return name.includes(query) ||
               address.includes(query) ||
               email.includes(query) ||
               (queryDigits && phone.includes(queryDigits));
      });
    }

    // Dollar range — inclusive on both ends
    const min = minDollar ? parseFloat(minDollar) : null;
    const max = maxDollar ? parseFloat(maxDollar) : null;
    if (min != null || max != null) {
      list = list.filter(est => {
        const t = est.estimate_data?.total;
        if (t == null) return false;   // no dollar amount = can't judge
        if (min != null && t < min) return false;
        if (max != null && t > max) return false;
        return true;
      });
    }

    // Includes a specific service — substring match against line-item
    // names. "sliding" matches "New Sliding Screen Door" and "Sliding
    // Screen Rescreen"; the filter dropdown will offer common values
    // but any typed substring works too.
    const svc = includesService.trim().toLowerCase();
    if (svc) {
      list = list.filter(est => {
        const items = est.estimate_data?.line_items || [];
        return items.some(li => (li.name || "").toLowerCase().includes(svc));
      });
    }

    // Zip code — comma-separated list, matched via digit-substring
    // against the address so it works whether the zip is stored on
    // its own or embedded in the full address string.
    const zips = zipFilter.split(",").map(z => z.trim().replace(/\D/g, "")).filter(z => z.length >= 3);
    if (zips.length > 0) {
      list = list.filter(est => {
        const addr = (est.address || "").replace(/\D/g, "");
        return zips.some(z => addr.includes(z));
      });
    }

    return list;
  })();

  // Apply status filter for display (separate from stats)
  const displayedEstimates = (() => {
    if (!statusFilter) return searchFilteredEstimates; // "All" - show everything
    if (statusFilter === "active") {
      return searchFilteredEstimates.filter(est => !["converted", "expired", "lost"].includes(est.estimate_status));
    }
    // For "hot" filter
    if (statusFilter === "hot") {
      return searchFilteredEstimates.filter(est => est.estimate_status === "hot");
    }
    // For "phone_follow_up" filter
    if (statusFilter === "phone_follow_up") {
      return searchFilteredEstimates.filter(est => est.estimate_status === "phone_follow_up");
    }
    // For "drip_finished" filter
    if (statusFilter === "drip_finished") {
      return searchFilteredEstimates.filter(est => est.estimate_status === "drip_finished");
    }
    // High-value filter: open estimates (not converted/expired/lost) whose
    // total is at or above the configured threshold. Sorted by dollar
    // amount descending so the biggest fish surface at the top.
    if (statusFilter === "high_value") {
      return searchFilteredEstimates
        .filter(est => !["converted", "expired", "lost"].includes(est.estimate_status))
        .filter(est => (est.estimate_data?.total || 0) >= highValueThreshold)
        .sort((a, b) => (b.estimate_data?.total || 0) - (a.estimate_data?.total || 0));
    }
    // For "new" filter, also include "sent" (legacy status)
    if (statusFilter === "new") {
      return searchFilteredEstimates.filter(est => ["new", "sent", null, undefined].includes(est.estimate_status) || !est.estimate_status);
    }
    // For "nurturing" filter, also include legacy "followed_up"
    if (statusFilter === "nurturing") {
      return searchFilteredEstimates.filter(est => ["nurturing", "followed_up"].includes(est.estimate_status));
    }
    // Scheduled tab — sort by upcoming appointment ascending so the
    // soonest job surfaces first. Falls back to preferred_date/time when
    // scheduled_job_date is missing; missing-both goes to the bottom.
    if (statusFilter === "scheduled") {
      const apptKey = (est) => {
        const d = est.scheduled_job_date || est.preferred_date;
        if (!d) return "9999-12-31T99:99";
        const t = est.scheduled_job_time || est.preferred_time || "00:00";
        return `${d}T${t}`;
      };
      return searchFilteredEstimates
        .filter(est => est.estimate_status === "scheduled")
        .sort((a, b) => apptKey(a).localeCompare(apptKey(b)));
    }
    return searchFilteredEstimates.filter(est => est.estimate_status === statusFilter);
  })();

  const getEstimateStatus = (estimate) => {
    const status = estimate.estimate_status || "new";
    // Map legacy statuses
    if (status === "sent") return "new";
    if (status === "followed_up") return "nurturing";
    return status;
  };

  const getEstimateStatusDisplay = (estimate) => {
    const status = getEstimateStatus(estimate);
    
    // If scheduled, show the actual job date (from soonest upcoming job)
    if (status === "scheduled") {
      const jobDate = estimate.scheduled_job_date || estimate.preferred_date;
      const jobTime = estimate.scheduled_job_time || estimate.preferred_time;
      
      if (jobDate) {
        const date = new Date(jobDate + 'T12:00:00').toLocaleDateString('en-US', {
          weekday: 'short', month: 'short', day: 'numeric',
          timeZone: 'America/Los_Angeles'
        });
        return `Scheduled: ${date}${jobTime ? ` @ ${jobTime}` : ""}`;
      }
      return "Scheduled";
    }
    
    return STATUS_LABELS[status] || status;
  };

  const getFollowUpInfo = (estimate) => {
    const count = estimate.follow_up_count || 0;
    const lastFollowUp = estimate.last_followed_up_at;
    const followUpType = estimate.last_follow_up_type;
    
    // Always return count info, even if no follow-ups yet
    if (!lastFollowUp) {
      return { count, timeAgo: null, isAuto: false, lastDate: null };
    }
    
    // Parse the date properly - handle both ISO string and other formats
    let lastDate;
    if (typeof lastFollowUp === 'string') {
      // If it's an ISO string with Z, parse it directly
      // If it doesn't have timezone, assume UTC
      if (lastFollowUp.includes('Z') || lastFollowUp.includes('+')) {
        lastDate = new Date(lastFollowUp);
      } else {
        // Append Z to treat as UTC
        lastDate = new Date(lastFollowUp + 'Z');
      }
    } else {
      lastDate = new Date(lastFollowUp);
    }
    
    // Check if valid date
    if (isNaN(lastDate.getTime())) {
      console.warn('Invalid follow-up date:', lastFollowUp);
      return { count, timeAgo: 'Unknown', isAuto: followUpType === 'auto', lastDate: null };
    }
    
    const now = new Date();
    const diffMs = now.getTime() - lastDate.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    let timeAgo;
    if (diffDays > 0) {
      timeAgo = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      timeAgo = `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMinutes > 5) {
      timeAgo = `${diffMinutes} min ago`;
    } else {
      timeAgo = 'Just now';
    }
    
    return {
      count,
      timeAgo,
      isAuto: followUpType === 'auto',
      lastDate
    };
  };

  // Navigate to messages for a customer. We always append the phone +
  // name to the URL so Messages.js can fall back to phone matching when
  // the estimate's stored customer_id is stale (a dedup rewrite that
  // left the thread owned by a different customer record with the same
  // phone — exactly the "clicked Message, got New Message picker"
  // scenario Brett kept hitting on Hot leads).
  const handleOpenMessages = async (estimate) => {
    const phoneQS = estimate.phone
      ? `&phone=${encodeURIComponent(estimate.phone)}`
      : "";
    const nameQS = estimate.full_name
      ? `&name=${encodeURIComponent(estimate.full_name)}`
      : "";

    if (estimate.customer_id) {
      navigate(`/messages?customer=${estimate.customer_id}${phoneQS}${nameQS}`);
      return;
    }
    // No customer_id on the estimate — resolve by phone first.
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { phone: estimate.phone },
      });

      const customers = response.data;
      if (customers && customers.length > 0) {
        navigate(
          `/messages?customer=${customers[0].customer_id}${phoneQS}${nameQS}`
        );
      } else {
        // Brand new contact — Messages.js will open a prefilled composer.
        navigate(
          `/messages?phone=${encodeURIComponent(estimate.phone)}${nameQS}`
        );
      }
    } catch (error) {
      toast.error("Could not find customer. Please start conversation from Messages tab.");
      navigate('/messages');
    }
  };

  // Handle manual follow-up
  const handleFollowUp = async (leadId) => {
    setFollowUpLoading(leadId);
    try {
      const token = getSessionToken();
      if (!token) {
        toast.error("Please log in again");
        return;
      }
      
      const response = await axios.post(`${API}/estimates/${leadId}/follow-up`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success === false) {
        toast.error(response.data.error || "Failed to record follow-up");
      } else {
        toast.success(`Follow-up recorded (${response.data.follow_up_count} total)`);
        fetchEstimates();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.detail || "Failed to record follow-up");
    } finally {
      setFollowUpLoading(null);
    }
  };

  // Handle sequence start
  const handleStartSequence = async (leadId) => {
    setSequenceLoading(leadId);
    try {
      const token = getSessionToken();
      await axios.post(`${API}/estimates/${leadId}/sequence/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Follow-up sequence started");
      fetchEstimates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start sequence");
    } finally {
      setSequenceLoading(null);
    }
  };

  // Handle sequence stop
  const handleStopSequence = async (leadId) => {
    setSequenceLoading(leadId);
    try {
      const token = getSessionToken();
      await axios.post(`${API}/estimates/${leadId}/sequence/stop`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Follow-up sequence stopped");
      fetchEstimates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to stop sequence");
    } finally {
      setSequenceLoading(null);
    }
  };

  // Handle sequence resume
  const handleResumeSequence = async (leadId) => {
    setSequenceLoading(leadId);
    try {
      const token = getSessionToken();
      await axios.post(`${API}/estimates/${leadId}/sequence/resume`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Follow-up sequence resumed");
      fetchEstimates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resume sequence");
    } finally {
      setSequenceLoading(null);
    }
  };

  // Test follow-up message (preview or send)
  const handleTestFollowUp = async (leadId, stepIndex = 0, sendForReal = false) => {
    try {
      const token = getSessionToken();
      const response = await axios.post(`${API}/estimates/${leadId}/sequence/test`, {
        step_index: stepIndex,
        send_for_real: sendForReal
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      if (sendForReal) {
        toast.success(`Test message sent to ${data.would_send_to}`);
      } else {
        // Show preview in alert/toast
        console.log("Follow-up test result:", data);
        toast.info(
          `Preview (Step ${stepIndex + 1}):\n${data.resolved_message.substring(0, 100)}...`,
          { duration: 10000 }
        );
        // Also log the full result for debugging
        alert(`FRONTEND_URL env: ${data.frontend_url_env}\n\nMessage that would be sent:\n${data.resolved_message}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to test follow-up");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#207024]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      <Header user={user} />
      
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-barlow text-2xl font-bold text-zinc-900 uppercase">
              Estimates
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/estimates/nearby")}
              data-testid="nearby-estimates-btn"
              title="Find open estimates near an address or job"
              className="px-3 py-2 text-sm rounded-lg flex items-center gap-1 bg-zinc-100 hover:bg-zinc-200 text-zinc-700"
            >
              <MapPin className="w-4 h-4" />
              <span>Nearby</span>
            </button>
            <button
              onClick={() => openDupeSweep(24)}
              data-testid="dupe-sweep-btn"
              title="Find & merge duplicate estimates"
              className="px-3 py-2 text-sm rounded-lg flex items-center gap-1 bg-zinc-100 hover:bg-zinc-200 text-zinc-700"
            >
              <Copy className="w-4 h-4" />
              <span className="hidden sm:inline">Dupe Sweep</span>
            </button>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-testid="advanced-filters-trigger"
                  title="Advanced filters"
                  className={`px-3 py-2 text-sm rounded-lg flex items-center gap-1 relative ${
                    activeFilterCount > 0
                      ? "bg-[#207024] text-white hover:bg-[#1a5c1d]"
                      : "bg-zinc-100 hover:bg-zinc-200 text-zinc-700"
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  <span className="hidden sm:inline">Filters</span>
                  {activeFilterCount > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/25 text-[10px] font-bold">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-4 space-y-3">
                <div className="text-sm font-semibold text-zinc-800">Advanced Filters</div>

                <div>
                  <label className="text-xs font-medium text-zinc-600 mb-1 block">
                    Dollar amount
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-400 text-sm">$</span>
                    <input
                      type="number" min="0" step="50" placeholder="Min"
                      value={minDollar}
                      onChange={(e) => setMinDollar(e.target.value)}
                      data-testid="filter-min-dollar-input"
                      className="w-full h-9 px-2 rounded-md border-2 border-zinc-200 focus:border-[#207024] focus:outline-none text-sm"
                    />
                    <span className="text-zinc-400 text-xs">to</span>
                    <span className="text-zinc-400 text-sm">$</span>
                    <input
                      type="number" min="0" step="50" placeholder="Max"
                      value={maxDollar}
                      onChange={(e) => setMaxDollar(e.target.value)}
                      data-testid="filter-max-dollar-input"
                      className="w-full h-9 px-2 rounded-md border-2 border-zinc-200 focus:border-[#207024] focus:outline-none text-sm"
                    />
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Leave blank for no cap.</p>
                </div>

                <div>
                  <label className="text-xs font-medium text-zinc-600 mb-1 block">
                    Includes service
                  </label>
                  <input
                    list="filter-service-datalist"
                    type="text"
                    value={includesService}
                    onChange={(e) => setIncludesService(e.target.value)}
                    placeholder="e.g. Sliding Screen Door"
                    data-testid="filter-service-input"
                    className="w-full h-9 px-2 rounded-md border-2 border-zinc-200 focus:border-[#207024] focus:outline-none text-sm"
                  />
                  <datalist id="filter-service-datalist">
                    <option value="Sliding Screen Door" />
                    <option value="Rescreen" />
                    <option value="Screen Rescreen" />
                    <option value="Full Screen" />
                    <option value="Solar Screen" />
                    <option value="Pet Screen" />
                    {pricingItems.map(p => (
                      <option key={p.name} value={p.name} />
                    ))}
                  </datalist>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Substring match — &ldquo;sliding&rdquo; catches all sliding-door line items.</p>
                </div>

                <div>
                  <label className="text-xs font-medium text-zinc-600 mb-1 block">
                    Zip code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={zipFilter}
                    onChange={(e) => setZipFilter(e.target.value)}
                    placeholder="e.g. 92562 or 92562, 92563"
                    data-testid="filter-zip-input"
                    className="w-full h-9 px-2 rounded-md border-2 border-zinc-200 focus:border-[#207024] focus:outline-none text-sm"
                  />
                  <p className="text-[11px] text-zinc-400 mt-0.5">Separate multiple zips with commas.</p>
                </div>

                <button
                  onClick={clearAdvancedFilters}
                  disabled={activeFilterCount === 0}
                  data-testid="filter-clear-button"
                  className="w-full h-9 rounded-md bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-sm font-semibold disabled:opacity-40"
                >
                  Clear filters
                </button>
              </PopoverContent>
            </Popover>

            {user?.role === "Admin" && (
              <button
                onClick={runFakePhoneScan}
                disabled={fakeScanning}
                data-testid="scan-fake-phones-button"
                title="Scan active estimates for fake phone numbers"
                className="px-3 py-2 text-sm bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-lg flex items-center gap-1 disabled:opacity-50"
              >
                <ShieldCheck className="w-4 h-4" />
                <span className="hidden sm:inline">{fakeScanning ? "Scanning…" : "Scan Phones"}</span>
              </button>
            )}
          </div>
        </div>

        {/* Mini Dashboard - Row 1: Total, Converted, Conversion % */}
        <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-xl p-4 mb-2 text-white">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-zinc-700/50 rounded-lg p-3 border border-zinc-600">
              <div className="text-2xl font-bold text-white">{filteredStats.counts.total}</div>
              <div className="text-xs text-zinc-400">Estimates</div>
              <div
                className="text-[11px] text-zinc-300 mt-1 font-semibold"
                data-testid="estimates-all-time-value"
                title="Total $ value across every estimate — includes lost / expired"
              >
                ${Math.round(filteredStats.allTimeValue).toLocaleString()}
              </div>
            </div>
            <div className="bg-green-500/20 rounded-lg p-3 border border-green-500/30">
              <div className="text-2xl font-bold text-green-400">{filteredStats.counts.converted}</div>
              <div className="text-xs text-green-300/80 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Converted
              </div>
            </div>
            <div className="bg-blue-500/20 rounded-lg p-3 border border-blue-500/30">
              <div className="text-2xl font-bold text-blue-400">{filteredStats.conversionPercent}%</div>
              <div className="text-xs text-blue-300/80 flex items-center gap-1">
                <Percent className="w-3 h-3" />
                Conversion
              </div>
            </div>
          </div>
        </div>

        {/* Mini Dashboard - Row 2: Pending, Pipeline, Won Revenue */}
        <div className="bg-gradient-to-r from-zinc-800 to-zinc-700 rounded-xl p-4 mb-4 text-white">
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-amber-500/20 rounded-lg p-3 border border-amber-500/30">
              <div className="text-xl font-bold text-amber-400">${Math.round(filteredStats.scheduledValue).toLocaleString()}</div>
              <div className="text-xs text-amber-300/80 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Scheduled
              </div>
            </div>
            <div className="bg-purple-500/20 rounded-lg p-3 border border-purple-500/30">
              <div className="text-xl font-bold text-purple-400">${Math.round(filteredStats.pendingValue).toLocaleString()}</div>
              <div className="text-xs text-purple-300/80 flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                Pipeline
              </div>
            </div>
            <div className="bg-emerald-500/20 rounded-lg p-3 border border-emerald-500/30">
              <div className="text-xl font-bold text-emerald-400">${Math.round(filteredStats.wonRevenue).toLocaleString()}</div>
              <div className="text-xs text-emerald-300/80 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                Won
              </div>
            </div>
          </div>
        </div>

        {/* Search + Date Filter (75/25) */}
        <div className="flex gap-2 mb-4">
          <div className="relative" style={{ flex: "0 0 75%" }}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <input
              type="text"
              placeholder="Search by name, phone, address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-8 py-3 rounded-xl border border-zinc-200 bg-white text-sm focus:outline-none focus:border-[#207024] focus:ring-1 focus:ring-[#207024]"
              data-testid="estimate-search"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-zinc-100 rounded-full"
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            )}
          </div>
          <div style={{ flex: "0 0 calc(25% - 0.5rem)" }} className="min-w-0">
            <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
              <PopoverTrigger asChild>
                <button
                  className={`w-full px-2 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-1 ${
                    datePreset !== "all"
                      ? "bg-blue-100 text-blue-700 border border-blue-200"
                      : "bg-white border border-zinc-200 text-zinc-600"
                  }`}
                  data-testid="date-filter-button"
                >
                  <Calendar className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate text-xs">
                    {datePreset === "custom" && customDateRange.from && customDateRange.to
                      ? `${customDateRange.from.toLocaleDateString()}`
                      : DATE_PRESETS.find(p => p.value === datePreset)?.label || "All Time"
                    }
                  </span>
                  <ChevronDown className="w-3 h-3 flex-shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0 overflow-y-auto"
                style={{
                  maxHeight: "var(--radix-popover-content-available-height)",
                }}
                align="end"
                side="bottom"
                sideOffset={6}
                collisionPadding={12}
                avoidCollisions
              >
                <div className="p-2 border-b">
                  <div className="grid grid-cols-2 gap-1">
                    {DATE_PRESETS.filter(p => p.value !== "custom").map((preset) => (
                      <button
                        key={preset.value}
                        onClick={() => {
                          setDatePreset(preset.value);
                          if (preset.value !== "custom") {
                            setShowDatePicker(false);
                          }
                        }}
                        className={`px-3 py-2 text-sm rounded-lg text-left ${
                          datePreset === preset.value
                            ? "bg-[#207024] text-white"
                            : "hover:bg-zinc-100"
                        }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="p-3">
                  <div className="text-sm font-medium text-zinc-700 mb-2">Custom Range</div>
                  <CalendarComponent
                    mode="range"
                    selected={customDateRange}
                    onSelect={(range) => {
                      setCustomDateRange(range || { from: null, to: null });
                      if (range?.from && range?.to) {
                        setDatePreset("custom");
                      }
                    }}
                    numberOfMonths={1}
                    className="rounded-md border"
                  />
                  {customDateRange.from && customDateRange.to && (
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="mt-2 w-full py-2 bg-[#207024] text-white rounded-lg text-sm font-medium"
                    >
                      Apply Range
                    </button>
                  )}
                  {datePreset !== "all" && (
                    <button
                      onClick={() => {
                        setDatePreset("all");
                        setCustomDateRange({ from: null, to: null });
                        setShowDatePicker(false);
                      }}
                      className="mt-2 w-full py-2 text-sm text-zinc-500 hover:text-zinc-700 border border-zinc-200 rounded-lg"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          <button
            onClick={() => setStatusFilter("active")}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              statusFilter === "active"
                ? "bg-[#207024] text-white"
                : "bg-white border text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setStatusFilter("new")}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              statusFilter === "new"
                ? "bg-amber-500 text-white"
                : "bg-white border text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            New ({filteredStats.counts.new})
          </button>
          <button
            onClick={() => setStatusFilter("nurturing")}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              statusFilter === "nurturing"
                ? "bg-purple-500 text-white"
                : "bg-white border text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            Nurturing ({filteredStats.counts.nurturing})
          </button>
          <button
            onClick={() => setStatusFilter("scheduled")}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              statusFilter === "scheduled"
                ? "bg-cyan-500 text-white"
                : "bg-white border text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            Scheduled ({filteredStats.counts.scheduled})
          </button>
          <button
            onClick={() => setStatusFilter("reschedule_needed")}
            data-testid="estimates-tab-reschedule-needed"
            className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              statusFilter === "reschedule_needed"
                ? "bg-orange-500 text-white"
                : "bg-white border text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            Reschedule Needed ({filteredStats.counts.reschedule_needed})
          </button>
          <button
            onClick={() => setStatusFilter("converted")}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              statusFilter === "converted"
                ? "bg-green-500 text-white"
                : "bg-white border text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            Converted ({filteredStats.counts.converted})
          </button>
          <button
            onClick={() => setStatusFilter("lost")}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              statusFilter === "lost"
                ? "bg-red-500 text-white"
                : "bg-white border text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            Lost ({filteredStats.counts.lost})
          </button>
          <button
            onClick={() => setStatusFilter("hot")}
            data-testid="filter-hot"
            className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              statusFilter === "hot"
                ? "bg-rose-500 text-white"
                : "bg-white border text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            <Flame className="w-3 h-3 inline mr-1" />
            Hot ({filteredStats.counts.hot})
          </button>
          <button
            onClick={() => setStatusFilter("phone_follow_up")}
            data-testid="filter-phone-follow-up"
            className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              statusFilter === "phone_follow_up"
                ? "bg-blue-500 text-white"
                : "bg-white border text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            <Phone className="w-3 h-3 inline mr-1" />
            Call ({filteredStats.counts.phone_follow_up})
          </button>
          <button
            onClick={() => setStatusFilter("drip_finished")}
            data-testid="filter-drip-finished"
            className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              statusFilter === "drip_finished"
                ? "bg-yellow-500 text-white"
                : "bg-white border text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            Drip Done ({filteredStats.counts.drip_finished})
          </button>
          {/* High-value filter — open estimates ≥ configured threshold. */}
          {(() => {
            const highValueCount = searchFilteredEstimates.filter(est =>
              !["converted", "expired", "lost"].includes(est.estimate_status)
              && (est.estimate_data?.total || 0) >= highValueThreshold
            ).length;
            const thresholdLabel = highValueThreshold >= 1000
              ? `$${(highValueThreshold / 1000).toFixed(highValueThreshold % 1000 === 0 ? 0 : 1)}K+`
              : `$${highValueThreshold}+`;
            return (
              <button
                onClick={() => setStatusFilter("high_value")}
                data-testid="filter-high-value"
                className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                  statusFilter === "high_value"
                    ? "bg-emerald-600 text-white"
                    : "bg-white border text-zinc-600 hover:bg-zinc-50"
                }`}
              >
                <DollarSign className="w-3 h-3 inline mr-0.5" />
                {thresholdLabel} ({highValueCount})
              </button>
            );
          })()}
          {user?.role === "Admin" && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  data-testid="high-value-threshold-settings"
                  title="Adjust high-value threshold"
                  className="px-2 py-1.5 rounded-full text-sm font-semibold bg-white border text-zinc-500 hover:bg-zinc-50 whitespace-nowrap"
                >
                  <Settings2 className="w-3.5 h-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-4">
                <div className="text-sm font-semibold text-zinc-700 mb-2">High-Value Threshold</div>
                <div className="text-xs text-zinc-500 mb-3">
                  Estimates at or above this amount get a $ badge and appear in the High Value filter.
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-zinc-500">$</span>
                  <input
                    type="number"
                    min="0"
                    step="50"
                    value={thresholdDraft}
                    onChange={(e) => setThresholdDraft(e.target.value)}
                    data-testid="high-value-threshold-input"
                    className="flex-1 h-10 px-3 rounded-lg border-2 border-zinc-200 focus:border-[#207024] focus:outline-none text-base"
                  />
                </div>
                <button
                  onClick={saveHighValueThreshold}
                  disabled={savingThreshold || Number(thresholdDraft) === highValueThreshold}
                  data-testid="high-value-threshold-save"
                  className="w-full h-10 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold disabled:opacity-50 text-sm"
                >
                  {savingThreshold ? "Saving…" : "Save Threshold"}
                </button>
              </PopoverContent>
            </Popover>
          )}
          <button
            onClick={() => setStatusFilter(null)}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
              statusFilter === null
                ? "bg-zinc-800 text-white"
                : "bg-white border text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            All
          </button>
        </div>

        {/* Estimates List */}
        <div className="space-y-3" data-testid="estimates-list">
          {displayedEstimates.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border">
              <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-500">
                {estimates.length === 0 ? "No estimates yet" : "No estimates match this filter"}
              </p>
              <p className="text-sm text-zinc-400 mt-1">
                Try selecting a different filter or date range
              </p>
            </div>
          ) : (
            displayedEstimates.map((estimate) => {
              const status = getEstimateStatus(estimate);
              const statusDisplay = getEstimateStatusDisplay(estimate);
              const StatusIcon = STATUS_ICONS[status] || Clock;
              const estimateData = estimate.estimate_data || {};
              const followUpInfo = getFollowUpInfo(estimate);
              
              return (
                <div
                  key={estimate.lead_id}
                  data-testid={`estimate-card-${estimate.lead_id}`}
                  className="bg-white rounded-xl border p-4"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-zinc-900">{estimate.full_name}</span>
                        {/* Repeat Customer Badge - shows when customer has paid invoices AND active estimates */}
                        {estimate.is_repeat_customer && (
                          <span 
                            className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-semibold rounded-full"
                            title={`${estimate.customer_paid_invoices || 0} paid invoice(s), $${(estimate.customer_total_revenue || 0).toFixed(2)} revenue`}
                          >
                            Repeat ${estimate.customer_total_revenue > 0 ? `($${Math.round(estimate.customer_total_revenue)})` : ''}
                          </span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status]}`}>
                          <StatusIcon className="w-3 h-3 inline mr-1" />
                          {statusDisplay}
                        </span>
                        {/* High-value badge — flags estimates at or above the
                            admin-configured threshold so admins can prioritise
                            follow-up on high-ticket leads. */}
                        {(estimateData.total || 0) >= highValueThreshold && (
                          <span
                            data-testid={`high-value-badge-${estimate.lead_id}`}
                            className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-600 text-white"
                            title={`Estimate total ≥ $${highValueThreshold.toLocaleString()}`}
                          >
                            <DollarSign className="w-3 h-3 inline -mt-0.5" />
                            {highValueThreshold >= 1000
                              ? `${(highValueThreshold / 1000).toFixed(highValueThreshold % 1000 === 0 ? 0 : 1)}K+`
                              : `${highValueThreshold}+`}
                          </span>
                        )}
                        {estimate.is_landline && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 border border-blue-200">
                            <Phone className="w-3 h-3 inline mr-1" />
                            Landline
                          </span>
                        )}
                        <FlexibleScheduleBadge flexible={estimate.flexible_schedule} size="sm" />
                        {/* Below-minimum flow choice badge — tagged when a
                            customer with <3 screens picked an off-funnel
                            path on the booking page. */}
                        {estimate.flow_choice === "mail_in_new_build" && (
                          <span
                            className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"
                            title="Customer chose to provide measurements for new screens"
                          >
                            Mail-In
                          </span>
                        )}
                        {estimate.flow_choice === "drop_off_rescreen" && (
                          <span
                            className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200"
                            title="Customer wants to drop off screens for rescreening"
                          >
                            Drop-Off
                          </span>
                        )}
                        {/* Memorial Day tier badge — shown when the lead came
                            through the Memorial Day campaign. Helps admins see
                            promotional ROI at a glance. */}
                        {(() => {
                          const tier = estimateData.memorial_day_tier || 0;
                          if (!tier) return null;
                          const label =
                            tier === 3 ? "FREE Door"
                            : tier === 2 ? "$200 OFF"
                            : "$100 OFF";
                          return (
                            <span
                              data-testid={`memorial-tier-badge-${estimate.lead_id}`}
                              className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gradient-to-r from-red-50 via-white to-blue-50 text-[#b22234] border border-[#b22234]/40"
                              title={`Memorial Day Sale — Tier ${tier}`}
                            >
                              🇺🇸 MD: {label}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="text-sm text-zinc-500 mt-1">
                        {estimate.phone}
                      </div>
                      {estimate.address && estimate.address !== "Not provided yet" && (
                        <div className="text-sm text-zinc-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate max-w-[250px]" title={estimate.address}>{estimate.address}</span>
                        </div>
                      )}
                      {/* Follow-up info - always show count */}
                      <div className="text-xs text-indigo-600 mt-1 flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" />
                        <span>{followUpInfo?.count || 0} follow-up{(followUpInfo?.count || 0) !== 1 ? 's' : ''}</span>
                        {followUpInfo?.timeAgo && (
                          <>
                            <span className="text-zinc-400">•</span>
                            <span>Last: {followUpInfo.timeAgo}</span>
                          </>
                        )}
                        {followUpInfo?.isAuto && (
                          <span className="px-1 py-0.5 bg-indigo-100 text-indigo-500 rounded text-[10px]">auto</span>
                        )}
                      </div>
                      {/* Lead Source */}
                      {(estimate.channel || estimate.utm_source || estimate.referrer || estimate.gclid || estimate.fbclid) && (
                        <div className="text-xs text-amber-600 mt-1 flex items-center gap-1 flex-wrap">
                          <span className="font-medium">Source:</span>
                          <ChannelBadge lead={estimate} />
                          {estimate.utm_source && (
                            <span className="px-1.5 py-0.5 bg-amber-100 rounded">{estimate.utm_source}</span>
                          )}
                          {estimate.utm_campaign && (
                            <span className="px-1.5 py-0.5 bg-amber-50 rounded text-amber-500">{estimate.utm_campaign}</span>
                          )}
                          {!estimate.utm_source && estimate.gclid && (
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded">Google Ads</span>
                          )}
                          {!estimate.utm_source && estimate.fbclid && (
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded">Facebook</span>
                          )}
                          {!estimate.utm_source && !estimate.gclid && !estimate.fbclid && estimate.referrer && (
                            <span className="px-1.5 py-0.5 bg-zinc-100 rounded truncate max-w-[150px]" title={estimate.referrer}>
                              {(() => {
                                try {
                                  return new URL(estimate.referrer).hostname.replace('www.', '');
                                } catch {
                                  return estimate.referrer;
                                }
                              })()}
                            </span>
                          )}
                        </div>
                      )}
                      {/* Sequence Status — only the paused state gets a
                          badge (unusual state worth calling out). Active
                          state is redundant with the follow-up count + the
                          "Stop Auto" button in More, so we hide it to keep
                          the card compact. */}
                      {estimate.sequence_paused && (
                        <div className="text-xs mt-1 flex items-center gap-1">
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded flex items-center gap-1">
                            <Pause className="w-3 h-3" />
                            Auto paused (customer replied)
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-lg text-[#207024]">{(() => {
                        // Same self-consistent math as the details modal:
                        // recompute total from displayed pieces so a stale
                        // stored `estimate_data.total` (missing an admin
                        // discount) can't contradict the discount row
                        // rendered right below.
                        const sub = Number(estimateData.subtotal) || 0;
                        const tx = Number(estimateData.tax) || 0;
                        const dt = estimateData.discount_type;
                        const dv = Number(estimateData.discount_value) || 0;
                        let disc;
                        if (dt === "percent" && dv > 0) {
                          disc = Math.round(sub * (dv / 100) * 100) / 100;
                        } else {
                          disc = Number(estimateData.discount_amount) || 0;
                        }
                        // If the doc has no subtotal we can't recompute — fall back
                        return sub > 0
                          ? formatCurrency(Math.max(0, sub - disc + tx))
                          : formatCurrency(estimateData.total);
                      })()}</div>
                      <div className="text-xs text-zinc-500">{formatDateTime(estimate.created_at)}</div>
                    </div>
                  </div>

                  {/* Line items preview */}
                  <div className="text-sm text-zinc-600 mb-3">
                    {estimateData.line_items?.slice(0, 2).map((item, idx) => (
                      <span key={idx}>
                        {item.name} x{item.quantity}
                        {idx < Math.min(estimateData.line_items.length - 1, 1) && ", "}
                      </span>
                    ))}
                    {estimateData.line_items?.length > 2 && (
                      <span className="text-zinc-400"> +{estimateData.line_items.length - 2} more</span>
                    )}
                  </div>

                  {/* Sent/Follow up info */}
                  {estimate.estimate_sent_at && (
                    <div className="text-xs text-zinc-500 mb-3">
                      Sent: {formatDate(estimate.estimate_sent_at)}
                      {estimate.last_follow_up_at && estimate.last_follow_up_at !== estimate.estimate_sent_at && (
                        <span> • Last follow-up: {formatDate(estimate.last_follow_up_at)}</span>
                      )}
                    </div>
                  )}
                  
                  {/* Discount info if present — recompute for percent-type
                      discounts so a corrupt stored discount_amount can't
                      contradict the "10%" label. */}
                  {(() => {
                    const sub = Number(estimateData.subtotal) || 0;
                    const dt = estimateData.discount_type;
                    const dv = Number(estimateData.discount_value) || 0;
                    let disc;
                    if (dt === "percent" && dv > 0) {
                      disc = Math.round(sub * (dv / 100) * 100) / 100;
                    } else {
                      disc = Number(estimateData.discount_amount) || 0;
                    }
                    if (disc <= 0) return null;
                    return (
                      <div className="text-xs text-green-600 mb-3">
                        Discount: -{formatCurrency(disc)}
                        {dt === "percent" && ` (${dv}%)`}
                        {estimateData.discount_description && (
                          <span className="text-zinc-500 ml-1">— {estimateData.discount_description}</span>
                        )}
                      </div>
                    );
                  })()}

                  {(estimate.lead_tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2" data-testid={`estimate-tags-${estimate.lead_id}`}>
                      {estimate.lead_tags.map((t) => (
                        <span key={t} className="text-[11px] font-semibold bg-purple-100 text-purple-800 rounded-full px-2 py-0.5">{t}</span>
                      ))}
                    </div>
                  )}
                  {status === "lost" && estimate.lost_reason && (
                    <div className="text-xs text-red-700 bg-red-50 rounded-md px-2 py-1 mb-2" data-testid={`estimate-lost-reason-${estimate.lead_id}`}>
                      Closed: {estimate.lost_reason}
                    </div>
                  )}
                  {estimate.keep_reminder_at && status !== "lost" && (
                    <div className="text-xs text-amber-800 bg-amber-50 rounded-md px-2 py-1 mb-2" data-testid={`estimate-keep-reminder-${estimate.lead_id}`}>
                      Replied KEEP — follow up {new Date(estimate.keep_reminder_at).toLocaleDateString()}
                    </div>
                  )}

                  {/* Actions — 3 inline primaries + "More" popover so each
                      card stays compact. Context CTA rotates by status:
                      new → Send, active → Convert, converted → View
                      Invoice. Everything secondary lives in More. */}
                  <div className="flex gap-2 items-center flex-wrap">
                    <button
                      onClick={() => {
                        setSelectedEstimate(estimate);
                        setViewDialogOpen(true);
                      }}
                      className="px-3 py-1.5 text-sm bg-zinc-100 text-zinc-700 rounded-lg hover:bg-zinc-200"
                    >
                      <Eye className="w-4 h-4 inline mr-1" />
                      View
                    </button>

                    {/* Context CTA: Send / Convert / View Invoice */}
                    {status === "new" && (
                      <button
                        onClick={() => {
                          setSelectedEstimate(estimate);
                          setSendEmail(!!estimate.email);
                          setSendSMS(!!estimate.phone);
                          setSendDialogOpen(true);
                        }}
                        className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                      >
                        <Send className="w-4 h-4 inline mr-1" />
                        Send
                      </button>
                    )}
                    {status !== "new" && status !== "converted" && status !== "expired" && status !== "lost" && (
                      <button
                        onClick={() => handleConvertToInvoice(estimate)}
                        className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                      >
                        <ArrowRight className="w-4 h-4 inline mr-1" />
                        Convert
                      </button>
                    )}
                    {status === "converted" && estimate.converted_invoice_id && (
                      <button
                        onClick={() => navigate('/invoices')}
                        className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                      >
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        View Invoice
                      </button>
                    )}

                    <button
                      onClick={() => handleOpenMessages(estimate)}
                      className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                    >
                      <MessageSquare className="w-4 h-4 inline mr-1" />
                      Message
                    </button>

                    <button
                      onClick={() => openEditDialog(estimate)}
                      data-testid={`edit-estimate-${estimate.lead_id}`}
                      className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                    >
                      <Edit2 className="w-4 h-4 inline mr-1" />
                      Edit
                    </button>

                    {status !== "converted" && status !== "expired" && status !== "scheduled" && (
                      <button
                        onClick={() => handleMarkScheduled(estimate)}
                        data-testid={`mark-scheduled-${estimate.lead_id}`}
                        className="px-3 py-1.5 text-sm bg-cyan-100 text-cyan-700 rounded-lg hover:bg-cyan-200"
                      >
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Scheduled
                      </button>
                    )}

                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          data-testid={`more-actions-${estimate.lead_id}`}
                          className="px-2.5 py-1.5 text-sm bg-zinc-100 text-zinc-700 rounded-lg hover:bg-zinc-200 flex items-center gap-1"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                          More
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-56 p-1">
                        {status !== "new" && status !== "converted" && status !== "expired" && status !== "lost" && (
                          <MoreMenuItem
                            icon={Send}
                            label="Resend"
                            onClick={() => {
                              setSelectedEstimate(estimate);
                              setSendEmail(!!estimate.email);
                              setSendSMS(!!estimate.phone);
                              setSendDialogOpen(true);
                            }}
                          />
                        )}
                        {status === "new" && (
                          <MoreMenuItem
                            icon={ArrowRight}
                            label="Convert to Invoice"
                            onClick={() => handleConvertToInvoice(estimate)}
                          />
                        )}
                        <MoreMenuItem
                          icon={Phone}
                          label={estimate.last_called_at ? `Called ${formatRelative(estimate.last_called_at)}` : "Log Call"}
                          onClick={() => {
                            setSelectedEstimate(estimate);
                            setCallLogOpen(true);
                          }}
                          testId={`log-call-${estimate.lead_id}`}
                        />
                        {status === "new" && (
                          <MoreMenuItem
                            icon={RefreshCw}
                            label="Mark Nurturing"
                            onClick={() => handleUpdateStatus(estimate.lead_id, "nurturing")}
                          />
                        )}
                        {(status === "new" || status === "nurturing") && user?.role === "Admin" && (
                          estimate.sequence_paused ? (
                            <MoreMenuItem
                              icon={Play}
                              label="Resume Auto"
                              onClick={() => handleResumeSequence(estimate.lead_id)}
                              testId={`resume-sequence-${estimate.lead_id}`}
                            />
                          ) : estimate.sequence_active ? (
                            <MoreMenuItem
                              icon={Square}
                              label="Stop Auto"
                              onClick={() => handleStopSequence(estimate.lead_id)}
                              testId={`stop-sequence-${estimate.lead_id}`}
                            />
                          ) : (
                            <MoreMenuItem
                              icon={Play}
                              label="Start Auto"
                              onClick={() => handleStartSequence(estimate.lead_id)}
                              testId={`start-sequence-${estimate.lead_id}`}
                            />
                          )
                        )}
                        {status !== "converted" && status !== "expired" && status !== "lost" && (
                          <MoreMenuItem
                            icon={XCircle}
                            label="Mark Lost"
                            onClick={() => handleMarkLost(estimate)}
                            tone="danger"
                          />
                        )}
                        {status !== "converted" && status !== "expired" && status !== "lost" && status !== "hot" && (
                          <MoreMenuItem
                            icon={Flame}
                            label="Mark Hot"
                            onClick={() => handleUpdateStatus(estimate.lead_id, "hot")}
                            testId={`mark-hot-${estimate.lead_id}`}
                          />
                        )}
                        {status === "scheduled" && (
                          <MoreMenuItem
                            icon={CalendarClock}
                            label="Reschedule Needed"
                            onClick={() => handleUpdateStatus(estimate.lead_id, "reschedule_needed")}
                            testId={`mark-reschedule-needed-${estimate.lead_id}`}
                          />
                        )}
                        {status === "hot" && (
                          <MoreMenuItem
                            icon={RefreshCw}
                            label="Move to Nurturing"
                            onClick={() => handleUpdateStatus(estimate.lead_id, "nurturing")}
                            testId={`unmark-hot-${estimate.lead_id}`}
                          />
                        )}
                        <div className="my-1 border-t border-zinc-200" />
                        <MoreMenuItem
                          icon={Trash2}
                          label="Delete"
                          onClick={() => {
                            setSelectedEstimate(estimate);
                            setDeleteDialogOpen(true);
                          }}
                          testId={`delete-estimate-${estimate.lead_id}`}
                          tone="danger"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Delete Estimate Confirmation */}
      {/* Fake-phone scan review modal — admin approves each deletion. */}
      <Dialog open={fakeScanOpen} onOpenChange={setFakeScanOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col" data-testid="fake-scan-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-2xl uppercase flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-[#207024]" />
              Fake Phone Scan
            </DialogTitle>
            <DialogDescription>
              {fakeScanResult ? (
                fakeScanResult.flagged_count === 0
                  ? `Scanned ${fakeScanResult.scanned} active estimates — no fake numbers found.`
                  : `Scanned ${fakeScanResult.scanned} active estimates and flagged ${fakeScanResult.flagged_count} with suspicious phone numbers. Review each and delete the ones that are clearly fake.`
              ) : "Loading…"}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto -mx-2 px-2 space-y-2 py-2">
            {fakeScanResult?.flagged?.map((row) => (
              <div
                key={row.lead_id}
                className="p-3 rounded-lg border-2 border-red-100 bg-red-50/40"
                data-testid={`flagged-row-${row.lead_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-zinc-900 truncate">
                      {row.full_name || "(no name)"}
                    </div>
                    <div className="text-sm text-zinc-600 font-mono">{row.phone || "(no phone)"}</div>
                    <div className="mt-1 text-xs">
                      <span className="inline-block px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                        {row.reason}
                      </span>
                      <span className="ml-2 text-zinc-400">
                        via {row.source === "twilio_lookup" ? "Twilio" : "heuristic"}
                      </span>
                    </div>
                    {row.estimate_total != null && (
                      <div className="text-xs text-zinc-500 mt-0.5">
                        Estimate: ${Number(row.estimate_total).toLocaleString()} · {row.estimate_status}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => deleteFlaggedLead(row.lead_id)}
                    disabled={deletingLeadIds.has(row.lead_id)}
                    data-testid={`delete-flagged-${row.lead_id}`}
                    className="shrink-0 h-9 px-3 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50"
                  >
                    {deletingLeadIds.has(row.lead_id) ? "…" : "Delete"}
                  </button>
                </div>
              </div>
            ))}
            {fakeScanResult && fakeScanResult.flagged_count === 0 && (
              <div className="text-center py-8 text-zinc-500 text-sm">
                <ShieldCheck className="w-10 h-10 text-emerald-300 mx-auto mb-2" />
                No fake phone numbers found — your list is clean.
              </div>
            )}
          </div>
          <div className="pt-2 border-t flex justify-end">
            <button
              onClick={() => setFakeScanOpen(false)}
              className="h-10 px-4 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-sm font-semibold"
            >
              Done
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Estimate?</AlertDialogTitle>            <AlertDialogDescription>
              {selectedEstimate?.estimate_status === "converted" ? (
                <span className="text-amber-600 font-medium">
                  ⚠️ This estimate was CONVERTED to an invoice. Deleting it may affect your records. Are you sure?
                </span>
              ) : (
                <>This will permanently delete the estimate for {selectedEstimate?.full_name}. This action cannot be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeleteEstimate(selectedEstimate?.estimate_status === "converted")} 
              className="bg-red-600 hover:bg-red-700"
            >
              {selectedEstimate?.estimate_status === "converted" ? "Force Delete" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Estimate Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Estimate Details</DialogTitle>
            <DialogDescription className="sr-only">View estimate details</DialogDescription>
          </DialogHeader>
          
          {selectedEstimate && (
            <div className="space-y-4 py-4">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-semibold text-zinc-900">{selectedEstimate.full_name}</div>
                  <div className="text-sm text-zinc-500">{selectedEstimate.email}</div>
                  <div className="text-sm text-zinc-500">{selectedEstimate.phone}</div>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${STATUS_COLORS[getEstimateStatus(selectedEstimate)]}`}>
                  {getEstimateStatus(selectedEstimate).replace('_', ' ')}
                </span>
              </div>

              <LeadSourcePicker lead={selectedEstimate}
                onChange={(v) => {
                  setSelectedEstimate({ ...selectedEstimate, marketing_source_id: v });
                  setEstimates((list) => list.map((e) => (e.lead_id === selectedEstimate.lead_id ? { ...e, marketing_source_id: v } : e)));
                }} />

              {/* Call log section — compact history + one-tap logger. Kept
                  near the top of the modal so the admin sees at a glance
                  when they last reached out. */}
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <Phone className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-sm font-semibold text-zinc-900">Call log</span>
                    {selectedEstimate.last_called_at && (
                      <span className="text-xs text-zinc-600 truncate">
                        Last: {formatRelative(selectedEstimate.last_called_at)}
                        {selectedEstimate.last_call_note && <span className="text-zinc-500"> — "{selectedEstimate.last_call_note}"</span>}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCallLogOpen((v) => !v)}
                    data-testid="details-log-call-toggle"
                    className="text-xs font-semibold text-amber-700 underline underline-offset-2"
                  >
                    {callLogOpen ? "Close" : "+ Log call"}
                  </button>
                </div>
                {callLogOpen && (
                  <div className="mt-2 flex gap-2 items-start">
                    <input
                      type="text"
                      value={callNote}
                      onChange={(e) => setCallNote(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleLogCall(); }}
                      placeholder='Note (e.g. "left VM")'
                      data-testid="details-call-note-input"
                      className="flex-1 h-9 px-3 rounded-md border border-zinc-200 text-sm focus:outline-none focus:border-[#207024]"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleLogCall}
                      disabled={callLogSaving}
                      data-testid="details-call-save-btn"
                      className="h-9 px-3 rounded-md bg-[#207024] hover:bg-[#185a1c] disabled:bg-zinc-300 text-white text-sm font-semibold"
                    >
                      {callLogSaving ? "…" : "Save"}
                    </button>
                  </div>
                )}
                {selectedEstimate.call_logs && selectedEstimate.call_logs.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-amber-200 space-y-1 max-h-32 overflow-y-auto">
                    {[...selectedEstimate.call_logs]
                      .sort((a, b) => (b.logged_at || "").localeCompare(a.logged_at || ""))
                      .slice(0, 8)
                      .map((entry) => (
                        <div key={entry.logged_at} className="flex items-center justify-between text-xs text-zinc-700 group">
                          <div className="min-w-0 truncate">
                            <span className="font-medium">{new Date(entry.logged_at).toLocaleString()}</span>
                            {entry.note && <span className="text-zinc-500"> — {entry.note}</span>}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteCallLog(entry.logged_at)}
                            className="opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-600 ml-2 flex-shrink-0"
                            aria-label="Remove"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              <div className="text-sm text-zinc-600 bg-zinc-50 p-3 rounded-lg">
                {selectedEstimate.address}
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Line Items</h4>
                <div className="space-y-2">
                  {selectedEstimate.estimate_data?.line_items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>
                        {item.name} × {item.quantity}
                        {item.quantity > 1 && item.unit_price > 0 && (
                          <span className="text-zinc-400 ml-1">(${item.unit_price.toFixed(2)}/ea)</span>
                        )}
                        {item.taxable && <span className="text-zinc-400 ml-1">(taxable)</span>}
                      </span>
                      <span className="font-medium">{formatCurrency(item.line_total)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>{formatCurrency(selectedEstimate.estimate_data?.subtotal || selectedEstimate.subtotal_before_discount || selectedEstimate.subtotal)}</span>
                </div>
                {(() => {
                  // Discount can live on either the top-level lead doc OR
                  // inside estimate_data (backend writes the latter on edit).
                  // Check both so admin-added discounts always render.
                  const ed = selectedEstimate.estimate_data || {};
                  const subtotalForDiscount = Number(ed.subtotal ?? selectedEstimate.subtotal) || 0;
                  const discountType =
                    selectedEstimate.discount_type || ed.discount_type;
                  const discountValue =
                    Number(selectedEstimate.discount_value ?? ed.discount_value) || 0;
                  // When discount_type=percent, ALWAYS derive the dollar
                  // amount from (subtotal * value/100) so a corrupt stored
                  // discount_amount (e.g. legacy rows where an admin edit
                  // saved a mismatched value) can't produce a "10% -$71.90"
                  // contradiction on-screen.
                  let discountAmount;
                  if (discountType === "percent" && discountValue > 0) {
                    discountAmount = Math.round(subtotalForDiscount * (discountValue / 100) * 100) / 100;
                  } else {
                    discountAmount =
                      Number(selectedEstimate.discount_amount) ||
                      Number(ed.discount_amount) ||
                      Number(ed.discount) ||
                      0;
                  }
                  if (discountAmount <= 0) return null;
                  const discountDescription =
                    selectedEstimate.discount_description ||
                    ed.discount_description;
                  return (
                    <div
                      className="flex justify-between text-sm text-green-600"
                      data-testid="estimate-details-discount-row"
                    >
                      <span>
                        Discount
                        {discountType === "percent" && ` (${discountValue}%)`}
                        {discountDescription && ` - ${discountDescription}`}
                      </span>
                      <span>-{formatCurrency(discountAmount)}</span>
                    </div>
                  );
                })()}
                {(selectedEstimate.estimate_data?.tax > 0 || selectedEstimate.tax > 0) && (
                  <div className="flex justify-between text-sm">
                    <span>Tax</span>
                    <span>{formatCurrency(selectedEstimate.estimate_data?.tax || selectedEstimate.tax)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Estimated Total</span>
                  <span className="text-[#207024]">{(() => {
                    // Derive the Estimated Total live from the displayed
                    // parts so a stale/corrupt stored `total` (e.g. one
                    // that was never recomputed after an admin added a
                    // discount) can't show subtotal + tax while the
                    // discount row is displayed separately underneath.
                    const ed = selectedEstimate.estimate_data || {};
                    const sub = Number(ed.subtotal ?? selectedEstimate.subtotal) || 0;
                    const tx = Number(ed.tax ?? selectedEstimate.tax) || 0;
                    const dt = selectedEstimate.discount_type || ed.discount_type;
                    const dv = Number(selectedEstimate.discount_value ?? ed.discount_value) || 0;
                    let disc;
                    if (dt === "percent" && dv > 0) {
                      disc = Math.round(sub * (dv / 100) * 100) / 100;
                    } else {
                      disc =
                        Number(selectedEstimate.discount_amount) ||
                        Number(ed.discount_amount) ||
                        Number(ed.discount) ||
                        0;
                    }
                    return formatCurrency(Math.max(0, sub - disc + tx));
                  })()}</span>
                </div>
              </div>

              {selectedEstimate.notes && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Notes</h4>
                  <p className="text-sm text-zinc-600">{selectedEstimate.notes}</p>
                </div>
              )}

              {/* Lead Source Tracking */}
              {(selectedEstimate.channel || selectedEstimate.utm_source || selectedEstimate.utm_campaign || selectedEstimate.referrer || selectedEstimate.gclid || selectedEstimate.fbclid) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Lead Source</h4>
                  <div className="space-y-1 text-sm">
                    {selectedEstimate.channel && (
                      <div className="flex gap-2" data-testid="lead-channel-detail">
                        <span className="text-zinc-500 w-20">Channel:</span>
                        <span className="font-medium">{selectedEstimate.channel}
                          {selectedEstimate.last_touch_channel && selectedEstimate.last_touch_channel !== selectedEstimate.channel && (
                            <span className="text-zinc-500 font-normal"> (first) · {selectedEstimate.last_touch_channel} (last)</span>)}
                        </span>
                      </div>
                    )}
                    {selectedEstimate.utm_source && (
                      <div className="flex gap-2">
                        <span className="text-zinc-500 w-20">Source:</span>
                        <span className="font-medium">{selectedEstimate.utm_source}</span>
                      </div>
                    )}
                    {selectedEstimate.utm_medium && (
                      <div className="flex gap-2">
                        <span className="text-zinc-500 w-20">Medium:</span>
                        <span>{selectedEstimate.utm_medium}</span>
                      </div>
                    )}
                    {selectedEstimate.utm_campaign && (
                      <div className="flex gap-2">
                        <span className="text-zinc-500 w-20">Campaign:</span>
                        <span>{selectedEstimate.utm_campaign}</span>
                      </div>
                    )}
                    {selectedEstimate.utm_content && (
                      <div className="flex gap-2">
                        <span className="text-zinc-500 w-20">Content:</span>
                        <span>{selectedEstimate.utm_content}</span>
                      </div>
                    )}
                    {selectedEstimate.utm_term && (
                      <div className="flex gap-2">
                        <span className="text-zinc-500 w-20">Term:</span>
                        <span>{selectedEstimate.utm_term}</span>
                      </div>
                    )}
                    {selectedEstimate.gclid && (
                      <div className="flex gap-2">
                        <span className="text-zinc-500 w-20">Google:</span>
                        <span className="text-blue-600">Google Ads Click</span>
                      </div>
                    )}
                    {selectedEstimate.fbclid && (
                      <div className="flex gap-2">
                        <span className="text-zinc-500 w-20">Facebook:</span>
                        <span className="text-blue-600">Facebook Click</span>
                      </div>
                    )}
                    {selectedEstimate.referrer && !selectedEstimate.utm_source && (
                      <div className="flex gap-2">
                        <span className="text-zinc-500 w-20">Referrer:</span>
                        <span className="truncate" title={selectedEstimate.referrer}>
                          {(() => {
                            try {
                              return new URL(selectedEstimate.referrer).hostname;
                            } catch {
                              return selectedEstimate.referrer;
                            }
                          })()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Estimate Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Estimate</DialogTitle>
            <DialogDescription>Choose how to send the estimate to {selectedEstimate?.full_name}</DialogDescription>
          </DialogHeader>
          
          {selectedEstimate && (
            <div className="space-y-4 py-4">
              <div className="bg-zinc-50 rounded-lg p-4">
                <div className="font-bold text-lg">{selectedEstimate.full_name}</div>
                <div className="text-xl font-bold text-[#207024] mt-2">
                  {formatCurrency(selectedEstimate.estimate_data?.total)}
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    disabled={!selectedEstimate.email}
                    className="w-5 h-5 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
                  />
                  <Mail className="w-5 h-5 text-zinc-500" />
                  <div className="flex-1">
                    <div className="font-medium">Email</div>
                    <div className="text-sm text-zinc-500">
                      {selectedEstimate.email || "No email on file"}
                    </div>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50">
                  <input
                    type="checkbox"
                    checked={sendSMS}
                    onChange={(e) => setSendSMS(e.target.checked)}
                    disabled={!selectedEstimate.phone}
                    className="w-5 h-5 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
                  />
                  <MessageSquare className="w-5 h-5 text-zinc-500" />
                  <div className="flex-1">
                    <div className="font-medium">Text Message (SMS)</div>
                    <div className="text-sm text-zinc-500">
                      {selectedEstimate.phone || "No phone on file"}
                    </div>
                  </div>
                </label>
              </div>

              {/* Booking permission — every quote goes out with a link;
                  this toggle just decides whether the customer can book
                  their own appointment from it (default) or whether the
                  admin plans to book manually (view-only quote). */}
              <div className="border-t pt-4 mt-2">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50">
                  <input
                    type="checkbox"
                    checked={allowBooking}
                    onChange={(e) => setAllowBooking(e.target.checked)}
                    className="w-5 h-5 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
                    data-testid="allow-booking-checkbox"
                  />
                  <Link2 className="w-5 h-5 text-zinc-500" />
                  <div className="flex-1">
                    <div className="font-medium">Let them book from the link</div>
                    <div className="text-sm text-zinc-500">
                      {allowBooking
                        ? "Customer can view the quote AND book their appointment."
                        : "Customer can only VIEW the quote — you'll book manually."}
                    </div>
                  </div>
                </label>
                {!allowBooking && (
                  <p className="text-xs text-amber-600 mt-2 px-3" data-testid="view-only-notice">
                    ⚠️ View-only link — the customer will see the quote but won&apos;t see a &quot;Book Now&quot; button.
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setSendDialogOpen(false)}
                  className="flex-1 py-3 border-2 border-zinc-200 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendEstimate}
                  disabled={sending || (!sendEmail && !sendSMS)}
                  className="flex-1 py-3 bg-[#207024] text-white rounded-lg font-semibold disabled:opacity-50"
                >
                  {sending ? "Sending..." : "Send Estimate"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Estimate Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Estimate</DialogTitle>
            <DialogDescription className="sr-only">Create a new estimate</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Customer Selector */}
            <div>
              <label className="block font-semibold text-zinc-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                Select Customer
              </label>
              <select
                value={selectedCustomerId}
                onChange={(e) => handleCustomerSelect(e.target.value)}
                className="w-full h-11 rounded-lg border-2 border-zinc-200 px-3 focus:border-[#207024]"
              >
                <option value="">-- Enter manually --</option>
                {customers.map(customer => (
                  <option key={customer.customer_id} value={customer.customer_id}>
                    {customer.fullName} - {customer.phoneNumber}
                  </option>
                ))}
              </select>
            </div>

            {/* Customer Info */}
            <div>
              <label className="block font-semibold text-zinc-700 mb-2">
                Customer Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newEstimate.customer_name}
                onChange={(e) => setNewEstimate(prev => ({ ...prev, customer_name: e.target.value }))}
                placeholder="John Smith"
                className="w-full h-11 rounded-lg border-2 border-zinc-200 px-3 focus:border-[#207024]"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Email</label>
                <input
                  type="email"
                  value={newEstimate.customer_email}
                  onChange={(e) => setNewEstimate(prev => ({ ...prev, customer_email: e.target.value }))}
                  placeholder="john@email.com"
                  className="w-full h-10 rounded-lg border-2 border-zinc-200 px-3 text-sm focus:border-[#207024]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={newEstimate.customer_phone}
                  onChange={(e) => setNewEstimate(prev => ({ ...prev, customer_phone: e.target.value }))}
                  placeholder="(951) 555-1234"
                  className="w-full h-10 rounded-lg border-2 border-zinc-200 px-3 text-sm focus:border-[#207024]"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Address</label>
              <input
                type="text"
                value={newEstimate.customer_address}
                onChange={(e) => setNewEstimate(prev => ({ ...prev, customer_address: e.target.value }))}
                placeholder="123 Main St, City, CA 92xxx"
                className="w-full h-10 rounded-lg border-2 border-zinc-200 px-3 text-sm focus:border-[#207024]"
              />
            </div>

            {/* Quick Add Preset Items */}
            <div className="border-t pt-4">
              <div className="text-sm font-semibold text-zinc-700 mb-2">Quick Add Services</div>
              <div className="flex flex-wrap gap-2">
                {pricingItems.filter(p => p.show_on_quick_add !== false).map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => addPresetLineItem(preset)}
                    type="button"
                    className="px-2 py-1 text-xs bg-zinc-100 hover:bg-zinc-200 rounded-full text-zinc-700"
                  >
                    + {preset.name} (${preset.unit_price})
                  </button>
                ))}
              </div>
            </div>

            {/* Line Items */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-zinc-700">Line Items</h4>
                <button
                  onClick={addLineItem}
                  type="button"
                  className="text-sm text-[#207024] hover:underline flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Add Custom Item
                </button>
              </div>
              
              <div className="space-y-3">
                {newEstimate.line_items.map((item, index) => (
                  <div key={index} className="bg-zinc-50 rounded-lg p-3 relative">
                    {newEstimate.line_items.length > 1 && (
                      <button
                        onClick={() => removeLineItem(index)}
                        type="button"
                        className="absolute top-2 right-2 text-zinc-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    <div className="mb-2">
                      <select
                        value={item.name}
                        onChange={(e) => {
                          if (e.target.value === "__custom__") {
                            updateLineItem(index, "name", "");
                          } else {
                            handlePresetSelect(index, e.target.value);
                          }
                        }}
                        className="w-full h-9 rounded border border-zinc-200 px-2 text-sm"
                      >
                        <option value="">-- Select service --</option>
                        {pricingItems.map(preset => (
                          <option key={preset.name} value={preset.name}>
                            {preset.name} - ${preset.unit_price}{preset.taxable ? " (taxable)" : ""}
                          </option>
                        ))}
                        <option value="__custom__">Custom item...</option>
                      </select>
                    </div>
                    {(item.name === "" || !pricingItems.find(p => p.name === item.name)) && (
                      <div className="mb-2">
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateLineItem(index, "name", e.target.value)}
                          placeholder="Custom service description"
                          className="w-full h-9 rounded border border-zinc-200 px-2 text-sm"
                        />
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-xs text-zinc-500">Qty</label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity === 0 ? "" : item.quantity}
                          onChange={(e) => {
                            const val = e.target.value;
                            updateLineItem(index, "quantity", val === "" ? 0 : parseInt(val) || 0);
                          }}
                          onBlur={(e) => {
                            // Ensure minimum of 1 when leaving field
                            if (!e.target.value || parseInt(e.target.value) < 1) {
                              updateLineItem(index, "quantity", 1);
                            }
                          }}
                          className="w-full h-9 rounded border border-zinc-200 px-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500">Unit Price</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price === 0 ? "" : item.unit_price}
                          onChange={(e) => {
                            // Empty string collapses to 0 so calculations
                            // stay well-defined. Rendering 0 as "" (above)
                            // lets the user type without fighting a stuck
                            // leading zero.
                            const raw = e.target.value;
                            const num = raw === "" ? 0 : parseFloat(raw);
                            updateLineItem(index, "unit_price", Number.isFinite(num) ? num : 0);
                          }}
                          placeholder="0.00"
                          className="w-full h-9 rounded border border-zinc-200 px-2 text-sm"
                        />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={item.taxable}
                            onChange={(e) => updateLineItem(index, "taxable", e.target.checked)}
                            className="w-4 h-4 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
                          />
                          <span className="text-zinc-600">Taxable</span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Discount */}
            <div className="border rounded-lg p-3 bg-zinc-50">
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                <DollarSign className="w-4 h-4 inline mr-1" />
                Discount (optional)
              </label>
              <div className="flex gap-2">
                <select
                  value={newEstimate.discount_type}
                  onChange={(e) => setNewEstimate(prev => ({ 
                    ...prev, 
                    discount_type: e.target.value,
                    discount_value: e.target.value ? prev.discount_value : 0
                  }))}
                  className="h-10 rounded border border-zinc-200 px-2 text-sm"
                >
                  <option value="">No discount</option>
                  <option value="dollar">$ Amount</option>
                  <option value="percent">% Percentage</option>
                </select>
                {newEstimate.discount_type && (
                  <div className="flex items-center gap-1">
                    {newEstimate.discount_type === "dollar" && <span className="text-zinc-500">$</span>}
                    <input
                      type="number"
                      min="0"
                      step={newEstimate.discount_type === "percent" ? "1" : "0.01"}
                      max={newEstimate.discount_type === "percent" ? "100" : undefined}
                      value={newEstimate.discount_value === 0 ? "" : newEstimate.discount_value}
                      onChange={(e) => setNewEstimate(prev => ({ 
                        ...prev, 
                        discount_value: e.target.value === "" ? 0 : parseFloat(e.target.value) || 0 
                      }))}
                      className="w-24 h-10 rounded border border-zinc-200 px-2 text-sm"
                      placeholder="0"
                    />
                    {newEstimate.discount_type === "percent" && <span className="text-zinc-500">%</span>}
                  </div>
                )}
              </div>
              {newEstimate.discount_type && (
                <input
                  type="text"
                  value={newEstimate.discount_description}
                  onChange={(e) => setNewEstimate(prev => ({ ...prev, discount_description: e.target.value }))}
                  className="w-full h-9 mt-2 rounded border border-zinc-200 px-2 text-sm"
                  placeholder="Discount reason (e.g., Senior, Military)"
                  data-testid="create-discount-description"
                />
              )}
            </div>

            {/* Totals Preview */}
            {(() => {
              const totals = calculateNewEstimateTotals();
              return totals.subtotal > 0 ? (
                <div className="border-t pt-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Subtotal</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </div>
                  {totals.discountAmount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount {newEstimate.discount_type === "percent" ? `(${newEstimate.discount_value}%)` : ""}</span>
                      <span>-{formatCurrency(totals.discountAmount)}</span>
                    </div>
                  )}
                  {totals.tax > 0 && (
                    <div className="flex justify-between text-zinc-500">
                      <span>Tax (8.75%)</span>
                      <span>{formatCurrency(totals.tax)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-base border-t pt-2">
                    <span>Estimated Total</span>
                    <span className="text-[#207024]">{formatCurrency(totals.total)}</span>
                  </div>
                </div>
              ) : null;
            })()}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Notes (optional)</label>
              <textarea
                value={newEstimate.notes}
                onChange={(e) => setNewEstimate(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                rows={2}
                className="w-full rounded-lg border-2 border-zinc-200 px-3 py-2 text-sm focus:border-[#207024]"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setCreateDialogOpen(false);
                  setSelectedCustomerId("");
                  setNewEstimate({
                    customer_name: "",
                    customer_email: "",
                    customer_phone: "",
                    customer_address: "",
                    notes: "",
                    line_items: [{ name: "", quantity: 1, unit_price: 0, taxable: false }],
                    discount_type: "",
                    discount_value: 0
                  });
                }}
                type="button"
                className="flex-1 py-3 border-2 border-zinc-200 rounded-lg font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateEstimate}
                type="button"
                className="flex-1 py-3 bg-[#207024] text-white rounded-lg font-semibold"
              >
                <Plus className="w-5 h-5 inline mr-2" />
                Create Estimate
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Estimate Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Edit Estimate</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Editing estimate for {selectedEstimate?.full_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Line Items */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-zinc-700">Line Items</label>
                <button
                  onClick={addLineItem}
                  type="button"
                  className="text-sm text-[#207024] hover:underline flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Add Line
                </button>
              </div>

              <div className="space-y-3">
                {newEstimate.line_items.map((item, index) => (
                  <div key={index} className="border rounded-lg p-3 bg-zinc-50 relative">
                    {newEstimate.line_items.length > 1 && (
                      <button
                        onClick={() => removeLineItem(index)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    <div className="mb-2">
                      <select
                        value={pricingItems.find(p => p.name === item.name) ? item.name : (item.name ? "__custom__" : "")}
                        onChange={(e) => {
                          if (e.target.value === "__custom__") {
                            updateLineItem(index, "name", "");
                          } else {
                            handlePresetSelect(index, e.target.value);
                          }
                        }}
                        className="w-full h-9 rounded border border-zinc-200 px-2 text-sm"
                      >
                        <option value="">-- Select service --</option>
                        {pricingItems.map(preset => (
                          <option key={preset.name} value={preset.name}>
                            {preset.name} - ${preset.unit_price}{preset.taxable ? " (taxable)" : ""}
                          </option>
                        ))}
                        <option value="__custom__">Custom item...</option>
                      </select>
                    </div>
                    {(item.name === "" || !pricingItems.find(p => p.name === item.name)) && (
                      <div className="mb-2">
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateLineItem(index, "name", e.target.value)}
                          placeholder="Custom service description"
                          className="w-full h-9 rounded border border-zinc-200 px-2 text-sm"
                        />
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-xs text-zinc-500">Qty</label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity === 0 ? "" : item.quantity}
                          onChange={(e) => {
                            const val = e.target.value;
                            updateLineItem(index, "quantity", val === "" ? 0 : parseInt(val) || 0);
                          }}
                          onBlur={(e) => {
                            if (!e.target.value || parseInt(e.target.value) < 1) {
                              updateLineItem(index, "quantity", 1);
                            }
                          }}
                          className="w-full h-9 rounded border border-zinc-200 px-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500">Unit Price</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price === 0 ? "" : item.unit_price}
                          onChange={(e) => {
                            // Empty string collapses to 0 so calculations
                            // stay well-defined. Rendering 0 as "" (above)
                            // lets the user type without fighting a stuck
                            // leading zero.
                            const raw = e.target.value;
                            const num = raw === "" ? 0 : parseFloat(raw);
                            updateLineItem(index, "unit_price", Number.isFinite(num) ? num : 0);
                          }}
                          placeholder="0.00"
                          className="w-full h-9 rounded border border-zinc-200 px-2 text-sm"
                        />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={item.taxable}
                            onChange={(e) => updateLineItem(index, "taxable", e.target.checked)}
                            className="w-4 h-4 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
                          />
                          <span className="text-zinc-600">Taxable</span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Discount */}
            <div className="border rounded-lg p-3 bg-zinc-50">
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                <DollarSign className="w-4 h-4 inline mr-1" />
                Discount (optional)
              </label>
              <div className="flex gap-2">
                <select
                  value={newEstimate.discount_type}
                  onChange={(e) => setNewEstimate(prev => ({ 
                    ...prev, 
                    discount_type: e.target.value,
                    discount_value: e.target.value ? prev.discount_value : 0
                  }))}
                  className="h-10 rounded border border-zinc-200 px-2 text-sm"
                >
                  <option value="">No discount</option>
                  <option value="dollar">$ Amount</option>
                  <option value="percent">% Percentage</option>
                </select>
                {newEstimate.discount_type && (
                  <div className="flex items-center gap-1">
                    {newEstimate.discount_type === "dollar" && <span className="text-zinc-500">$</span>}
                    <input
                      type="number"
                      min="0"
                      step={newEstimate.discount_type === "percent" ? "1" : "0.01"}
                      max={newEstimate.discount_type === "percent" ? "100" : undefined}
                      value={newEstimate.discount_value === 0 ? "" : newEstimate.discount_value}
                      onChange={(e) => setNewEstimate(prev => ({ 
                        ...prev, 
                        discount_value: e.target.value === "" ? 0 : parseFloat(e.target.value) || 0 
                      }))}
                      className="w-24 h-10 rounded border border-zinc-200 px-2 text-sm"
                      placeholder="0"
                    />
                    {newEstimate.discount_type === "percent" && <span className="text-zinc-500">%</span>}
                  </div>
                )}
              </div>
              {newEstimate.discount_type && (
                <input
                  type="text"
                  value={newEstimate.discount_description}
                  onChange={(e) => setNewEstimate(prev => ({ ...prev, discount_description: e.target.value }))}
                  className="w-full h-9 mt-2 rounded border border-zinc-200 px-2 text-sm"
                  placeholder="Discount reason (e.g., Senior, Military)"
                  data-testid="edit-discount-description"
                />
              )}
            </div>

            {/* Totals Preview */}
            {(() => {
              const totals = calculateNewEstimateTotals();
              return totals.subtotal > 0 ? (
                <div className="border-t pt-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Subtotal</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </div>
                  {totals.discountAmount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount {newEstimate.discount_type === "percent" ? `(${newEstimate.discount_value}%)` : ""}</span>
                      <span>-{formatCurrency(totals.discountAmount)}</span>
                    </div>
                  )}
                  {totals.tax > 0 && (
                    <div className="flex justify-between text-zinc-500">
                      <span>Tax (8.75%)</span>
                      <span>{formatCurrency(totals.tax)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-base border-t pt-2">
                    <span>Estimated Total</span>
                    <span className="text-[#207024]">{formatCurrency(totals.total)}</span>
                  </div>
                </div>
              ) : null;
            })()}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Notes (optional)</label>
              <textarea
                value={newEstimate.notes}
                onChange={(e) => setNewEstimate(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                rows={2}
                className="w-full rounded-lg border-2 border-zinc-200 px-3 py-2 text-sm focus:border-[#207024]"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setEditDialogOpen(false);
                  setSelectedEstimate(null);
                  setNewEstimate({
                    customer_name: "",
                    customer_email: "",
                    customer_phone: "",
                    customer_address: "",
                    notes: "",
                    line_items: [{ name: "", quantity: 1, unit_price: 0, taxable: false }],
                    discount_type: "",
                    discount_value: 0
                  });
                }}
                type="button"
                className="flex-1 py-3 border-2 border-zinc-200 rounded-lg font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateEstimate}
                type="button"
                className="flex-1 py-3 bg-[#207024] text-white rounded-lg font-semibold"
              >
                <CheckCircle className="w-5 h-5 inline mr-2" />
                Save Changes
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============== DUPE SWEEP DIALOG ============== */}
      <Dialog open={dupeOpen} onOpenChange={setDupeOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5 text-[#207024]" />
              Duplicate Estimate Sweep
            </DialogTitle>
            <DialogDescription>
              Leads sharing the same phone number, created within the selected window.
              Merging keeps the <strong>newest</strong> estimate and deletes the older ones.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 py-3 border-b border-zinc-200">
            <label className="text-xs font-semibold text-zinc-600">Window:</label>
            {[24, 72, 168, 720].map(h => (
              <button
                key={h}
                data-testid={`dupe-window-${h}`}
                onClick={() => { setDupeHours(h); openDupeSweep(h); }}
                className={`px-3 py-1.5 text-xs rounded-full font-semibold ${
                  dupeHours === h ? "bg-[#207024] text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                }`}
              >
                {h === 24 ? "24h" : h === 72 ? "3d" : h === 168 ? "7d" : "30d"}
              </button>
            ))}
            <span className="ml-auto text-xs text-zinc-500">
              {dupeLoading ? "Scanning…" : `${dupeGroups.length} group${dupeGroups.length === 1 ? "" : "s"}`}
            </span>
          </div>

          {dupeLoading ? (
            <div className="py-10 flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#207024]" />
            </div>
          ) : dupeGroups.length === 0 ? (
            <div className="py-12 text-center text-zinc-500 text-sm">
              <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
              No duplicates in the last {dupeHours === 24 ? "24 hours" : dupeHours === 72 ? "3 days" : dupeHours === 168 ? "7 days" : "30 days"} — you&apos;re clean.
            </div>
          ) : (
            <div className="space-y-4 py-3">
              {dupeGroups.map((g) => (
                <div key={g.normalized_phone} data-testid={`dupe-group-${g.normalized_phone}`} className="border-2 border-amber-200 bg-amber-50/40 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="text-sm font-bold text-zinc-900">{g.keeper.full_name || "Unknown"}</div>
                      <div className="text-xs text-zinc-500 font-mono">{g.keeper.phone} · {g.count} estimates</div>
                    </div>
                    <button
                      onClick={() => mergeGroup(g)}
                      disabled={mergingKey === g.normalized_phone}
                      data-testid={`dupe-merge-${g.normalized_phone}`}
                      className="px-3 py-1.5 text-xs bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold rounded-lg disabled:opacity-60"
                    >
                      {mergingKey === g.normalized_phone ? "Merging…" : `Keep newest, delete ${g.duplicates.length}`}
                    </button>
                  </div>

                  <div className="rounded-md bg-emerald-100/60 border border-emerald-300 px-3 py-2 mb-2">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-800">Keeper (newest)</div>
                    <div className="text-xs text-zinc-800 mt-0.5">
                      <span className="font-mono">{g.keeper.lead_id}</span> · {formatDateTime(g.keeper.created_at)} · ${g.keeper.estimate_total?.toFixed(0) || 0} · {g.keeper.estimate_status || "new"}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-red-700">Will be deleted</div>
                    {g.duplicates.map(d => (
                      <div key={d.lead_id} className="text-xs text-zinc-700 rounded-md bg-red-100/40 border border-red-200 px-3 py-1.5 flex items-center justify-between">
                        <span className="font-mono">{d.lead_id}</span>
                        <span className="text-zinc-500">{formatDateTime(d.created_at)} · ${d.estimate_total?.toFixed(0) || 0}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}
